import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import LeadRotationPanel from '@/components/LeadRotationPanel';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import EmailComposer from '@/components/EmailComposer';
import SMSComposer from '@/components/SMSComposer';
import CalComBookingModal from '@/components/CalComBookingModal';
import RequestApprovalDialog from '@/components/RequestApprovalDialog';
import {
  ArrowLeft, Phone, Mail, MessageSquare, Edit, Save, X, Building, MapPin, Globe,
  Target, User, Calendar, CheckSquare, FileText, Activity, Sparkles, TrendingUp,
  Clock, PlayCircle, PauseCircle, RefreshCw, ExternalLink, Send, Plus, ChevronRight,
  MoreHorizontal, PhoneCall, Bot, Zap, AlertCircle, CheckCircle, CalendarCheck,
  UserPlus, MailOpen, MessageCircle, Loader2, PhoneIncoming, Briefcase, Star,
  ArrowUpRight, Copy, History, Video, PhoneOutgoing, Check, Tag, Hash, Type, Bell,
  ShieldCheck, Compass,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Status colors
const statusStyles = {
  new: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  contacted: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
  qualified: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  opportunity: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  customer: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  meeting_scheduled: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500' },
  not_interested: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
  lost: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500' },
};

/**
 * Normalize a date string to ensure it's treated as UTC.
 * If the date string doesn't have timezone info, assume it's UTC and add the suffix.
 * This ensures JavaScript's Date() interprets it correctly.
 */
// Wrap a backend timestamp (string or Date) in a JS Date whose
// internal UTC moment matches what the display formatters use.
// Without this, naive ISO strings like "2026-05-06T14:16:00" get
// interpreted in the browser's local timezone, which makes the
// Timeline tab disagree with the All Activities tab by exactly the
// browser's UTC offset.
const toUTCDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  // normalizeToUTC adds +00:00 to naive strings so they're parsed
  // as UTC; here we build the Date from that canonical form.
  const normalized = (() => {
    if (typeof input !== 'string') return null;
    if (input.includes('T') && (input.includes('+') || input.includes('Z'))) {
      return input.replace('Z', '+00:00');
    }
    if (input.includes('T')) return input + '+00:00';
    return input;
  })();
  const d = new Date(normalized || input);
  return isNaN(d.getTime()) ? null : d;
};

const normalizeToUTC = (dateString) => {
  if (!dateString) return null;
  // If it's already a Date object, return its ISO string
  if (dateString instanceof Date) {
    return dateString.toISOString();
  }
  // If string contains 'T' (ISO format) but no timezone info
  if (typeof dateString === 'string' && dateString.includes('T')) {
    // Already has timezone info
    if (dateString.includes('+') || dateString.includes('Z')) {
      return dateString.replace('Z', '+00:00');
    }
    // No timezone - assume UTC
    return dateString + '+00:00';
  }
  return dateString;
};

// Date formatters with Dubai timezone (GMT+4)
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const normalized = normalizeToUTC(dateString);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai'
  });
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const normalized = normalizeToUTC(dateString);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Dubai'
  });
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const normalized = normalizeToUTC(dateString);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai'
  });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const normalized = normalizeToUTC(dateString);
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '';

  // Get current time in Dubai
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  // Older than a week — show the full date AND time of day so the
  // timeline reads "May 6, 2026, 02:32 PM" instead of just "May 6,
  // 2026" with no clue what time the event happened.
  return formatDateTime(dateString);
};

const formatCallDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// Source Attribution helpers + cards
// ---------------------------------------------------------------------------
// Read an attribution field from the contact, falling back through the
// common alternative shapes — flat field, nested utm_tracking object,
// custom_fields bag. Marketing data lands in CRMs from many vendors and
// each one names things slightly differently; this function hides that
// mess from the components below.
const attr = (contact, ...candidates) => {
  if (!contact) return null;
  const utm = contact.utm_tracking || {};
  const cf = contact.custom_fields || {};
  for (const key of candidates) {
    if (contact[key] != null && contact[key] !== '') return contact[key];
    if (utm[key] != null && utm[key] !== '') return utm[key];
    if (cf[key] != null && cf[key] !== '') return cf[key];
  }
  return null;
};

// Build the "Lead Created From" breadcrumb segments. Returns an array of
// `{label, kind, mono?}` — segments are emitted only when we actually
// have a value so a half-attributed lead doesn't show "> >" gaps.
const buildLeadCreatedFromPath = (contact) => {
  if (!contact) return [];
  const parts = [];
  const src = attr(contact, 'lead_source', 'source', 'utm_source');
  if (src) parts.push({ label: src, kind: 'source' });
  const campaign = attr(contact, 'campaign_name', 'utm_campaign');
  if (campaign) parts.push({ label: campaign, kind: 'campaign' });
  const adset = attr(contact, 'adset_name');
  if (adset) parts.push({ label: adset, kind: 'adset' });
  const ad = attr(contact, 'ad_name', 'utm_content');
  if (ad) parts.push({ label: ad, kind: 'ad' });
  const sid = attr(contact, 'source_id', 'platform_lead_id');
  if (sid) parts.push({ label: sid, kind: 'id', mono: true });
  return parts;
};

// Inline breadcrumb component for the Hero card. Renders nothing when
// we have less than two segments — a lonely "Direct" badge is already
// covered by the Source KPI tile next to it.
const LeadCreatedFromBreadcrumb = ({ contact }) => {
  const parts = buildLeadCreatedFromPath(contact);
  if (parts.length < 2) return null;
  return (
    <div className="mt-2 flex items-center flex-wrap gap-x-1.5 gap-y-1 text-[11px] text-slate-500" data-testid="lead-created-from">
      <span className="uppercase tracking-wide font-semibold text-slate-400 mr-1">
        Lead Created From
      </span>
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 && <ChevronRight size={10} className="text-slate-300" />}
          <span
            className={
              p.mono
                ? 'font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200'
                : 'text-slate-700 capitalize'
            }
          >
            {String(p.label).replace(/_/g, ' ')}
          </span>
        </span>
      ))}
    </div>
  );
};

// One labelled row in the attribution card. Keeps the layout uniform
// and avoids 20 copies of the same span/p markup.
const AttrRow = ({ label, value, mono = false, copyable = false }) => {
  if (value == null || value === '') return null;
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied`);
    } catch (_) {
      // clipboard may be unavailable in some browsers — soft fail
    }
  };
  return (
    <div className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-[11px] text-slate-500 shrink-0">{label}</span>
      <span className="text-xs text-slate-800 text-right min-w-0 break-all flex items-center gap-1">
        <span className={mono ? 'font-mono' : ''}>{String(value)}</span>
        {copyable && (
          <button onClick={onCopy} className="text-slate-400 hover:text-slate-700 shrink-0" title="Copy">
            <Copy size={10} />
          </button>
        )}
      </span>
    </div>
  );
};

// Source Attribution card — the system-of-record view a marketing
// agent reaches for when debugging "where did this lead come from".
// Hides entire sub-sections when they're empty so we don't show a card
// full of "—".
const SourceAttributionCard = ({ contact }) => {
  if (!contact) return null;

  const sourceFields = {
    'Lead Source':        attr(contact, 'lead_source', 'source'),
    'Source ID':          attr(contact, 'source_id'),
    'Platform Lead ID':   attr(contact, 'platform_lead_id'),
    'Campaign Name':      attr(contact, 'campaign_name', 'utm_campaign'),
    'Campaign ID':        attr(contact, 'campaign_id'),
    'Ad Set Name':        attr(contact, 'adset_name'),
    'Ad Set ID':          attr(contact, 'adset_id'),
    'Ad Name':            attr(contact, 'ad_name'),
    'Ad ID':              attr(contact, 'ad_id'),
    'UTM Source':         attr(contact, 'utm_source'),
    'UTM Medium':         attr(contact, 'utm_medium'),
    'UTM Campaign':       attr(contact, 'utm_campaign'),
    'UTM Content':        attr(contact, 'utm_content'),
    'UTM Term':           attr(contact, 'utm_term'),
    'Landing Page':       attr(contact, 'landing_page'),
    'Referrer URL':       attr(contact, 'referrer_url'),
    'First Touch Source': attr(contact, 'first_touch_source'),
    'Last Touch Source':  attr(contact, 'last_touch_source'),
  };
  const intFields = {
    'Integration':      attr(contact, 'api_source', 'integration_source', 'source_system'),
    'API Source':       attr(contact, 'api_source'),
    'External Rec ID':  attr(contact, 'external_record_id'),
    'Sync Status':      attr(contact, 'sync_status', 'hubspot_sync_status'),
    'Synced At':        attr(contact, 'sync_timestamp', 'hubspot_last_synced_at'),
  };

  const hasSource = Object.values(sourceFields).some((v) => v != null && v !== '');
  const hasInt    = Object.values(intFields).some((v) => v != null && v !== '');

  // No data at all — render a friendly empty state with a hint about
  // how to populate. Showing nothing would just confuse the user.
  if (!hasSource && !hasInt) {
    return (
      <Card className="p-5 border-slate-200" data-testid="source-attribution-card">
        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
          <Compass size={14} className="text-violet-500" />
          Source Attribution
        </h3>
        <p className="text-xs text-slate-500">
          No campaign attribution captured yet. Source data populates from UTM-tagged
          links, Meta / Google lead forms, or manual entry on lead creation.
        </p>
      </Card>
    );
  }

  // Highlighted "where it came from" mono code at the top, then the
  // tidy two sub-sections.
  const sid = attr(contact, 'source_id') || attr(contact, 'platform_lead_id');
  return (
    <Card className="p-5 border-slate-200" data-testid="source-attribution-card">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
        <Compass size={14} className="text-violet-500" />
        Source Attribution
        {sid && (
          <Badge variant="outline" className="ml-auto font-mono text-[10px] border-violet-200 text-violet-700 bg-violet-50">
            {sid}
          </Badge>
        )}
      </h3>

      {hasSource && (
        <div className="space-y-0">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">
            Attribution
          </p>
          {Object.entries(sourceFields).map(([label, value]) => (
            <AttrRow
              key={label}
              label={label}
              value={value}
              mono={label.endsWith('ID') || label.includes('URL') || label === 'Landing Page'}
              copyable={label.endsWith('ID')}
            />
          ))}
        </div>
      )}

      {hasInt && (
        <div className="space-y-0 mt-4 pt-3 border-t border-slate-200">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">
            Integration Details
          </p>
          {Object.entries(intFields).map(([label, value]) => {
            // Sync timestamps deserve human-readable rendering.
            let v = value;
            if ((label === 'Synced At') && value) {
              const d = toUTCDate(value);
              if (d) v = d.toLocaleString('en-AE', { timeZone: 'Asia/Dubai' });
            }
            return (
              <AttrRow
                key={label}
                label={label}
                value={v}
                mono={label.endsWith('ID')}
                copyable={label.endsWith('ID')}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
};

// Source Journey card — a clean vertical timeline of the lead's
// journey from "received from <source>" through to whatever its
// current stage is. Drives off the activity timeline already loaded
// by the page so there's no extra fetch needed. Picks only the events
// that map to a journey stage; deeper detail is in the Timeline tab.
const SourceJourneyCard = ({ contact, timeline = [] }) => {
  if (!contact) return null;
  // Map activity types -> journey labels. We only surface signals
  // that move the lead forward; chat noise and audit entries stay
  // in the main Timeline tab.
  const STAGE_BY_TYPE = {
    lead_created:        'Lead received',
    contact_created:     'Lead received',
    lead_assigned:       'Assigned to Agent',
    assignment:          'Assigned to Agent',
    call:                'Contacted',
    sms:                 'Contacted',
    whatsapp:            'Contacted',
    email:               'Contacted',
    site_visit:          'Site Visit',
    meeting:             'Meeting Scheduled',
    booking:             'Booking',
    deal:                'Deal Created',
    stage_change:        'Stage Updated',
    status_change:       'Status Updated',
  };

  // De-dupe consecutive entries that map to the same journey label —
  // 50 "Contacted" rows from a call burst would otherwise drown out
  // the rest. Keep the earliest one per stage.
  const seen = new Set();
  const journey = [];

  // Always start with the lead-received synthesized event so the
  // breadcrumb sits at the top of the journey even if timeline is sparse.
  const src = attr(contact, 'lead_source', 'source') || 'Direct';
  const sid = attr(contact, 'source_id', 'platform_lead_id');
  const camp = attr(contact, 'campaign_name', 'utm_campaign');
  const adset = attr(contact, 'adset_name');
  const ad = attr(contact, 'ad_name', 'utm_content');
  journey.push({
    label: `Lead received from ${src}`,
    date:  contact.created_at,
    meta: [
      sid   && `Source ID: ${sid}`,
      camp  && `Campaign: ${camp}`,
      adset && `Ad Set: ${adset}`,
      ad    && `Ad: ${ad}`,
    ].filter(Boolean),
    accent: 'violet',
  });
  seen.add('Lead received');

  // The timeline state is already sorted recent-first; walk in
  // chronological order for the journey view.
  const ordered = [...timeline].reverse();
  for (const ev of ordered) {
    const stage = STAGE_BY_TYPE[ev.type] || STAGE_BY_TYPE[ev.activity_type];
    if (!stage) continue;
    if (seen.has(stage)) continue;
    seen.add(stage);
    journey.push({
      label: stage,
      date:  ev.date || ev.created_at,
      meta:  ev.description ? [String(ev.description).slice(0, 120)] : [],
      accent: stage === 'Booking' ? 'emerald'
            : stage === 'Site Visit' ? 'amber'
            : stage === 'Contacted'  ? 'blue'
            : 'slate',
    });
  }

  const accentBg = {
    violet:  'bg-violet-500',
    emerald: 'bg-emerald-500',
    amber:   'bg-amber-500',
    blue:    'bg-blue-500',
    slate:   'bg-slate-400',
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="source-journey-card">
      <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
        <Activity size={14} className="text-blue-500" />
        Source Journey
      </h3>
      <ol className="relative border-l border-slate-200 ml-1.5 space-y-3">
        {journey.map((s, i) => {
          const d = toUTCDate(s.date);
          const pretty = d
            ? d.toLocaleString('en-AE', {
                timeZone: 'Asia/Dubai',
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })
            : '';
          return (
            <li key={i} className="ml-3">
              <span className={`absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full ${accentBg[s.accent] || accentBg.slate}`} />
              <p className="text-xs text-slate-400">{pretty}</p>
              <p className="text-sm font-medium text-slate-900">{s.label}</p>
              {s.meta?.length > 0 && (
                <ul className="mt-0.5 text-[11px] text-slate-500 space-y-0.5">
                  {s.meta.map((m, j) => <li key={j}>{m}</li>)}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

const ContactDetailRedesigned = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  // Check if user can assign leads - only Kheldoune or Admin
  const allowedAssignmentEmails = [
    'z.kheldoune@fakhruddinproperties.com',
    'admin@treppanliving.ae'
  ];
  const canChangeOwner = user?.email && (
    allowedAssignmentEmails.includes(user.email.toLowerCase()) ||
    ['super_admin', 'admin', 'owner', 'account_owner'].includes(user.role?.toLowerCase().replace(' ', '_'))
  );

  // Core state
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('timeline');

  // Related data
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState([]);
  const [calls, setCalls] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [lifecycleStages, setLifecycleStages] = useState([]);
  const [hubspotSyncInfo, setHubspotSyncInfo] = useState(null);
  
  // Auto-determine department based on logged-in user
  // khelodoune (z.kheldoune@fakhruddinproperties.com) = Telesales, all others = Sales
  const userDepartment = user?.email?.toLowerCase() === 'z.kheldoune@fakhruddinproperties.com' ? 'telesales' : 'sales';
  const [selectedDepartment, setSelectedDepartment] = useState(userDepartment);
  
  // Custom Fields and Tags
  const [customFields, setCustomFields] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [contactTags, setContactTags] = useState([]);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [addingTag, setAddingTag] = useState(false);

  // Dialog states
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isOwnerDialogOpen, setIsOwnerDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);
  const [isLifecycleStageDialogOpen, setIsLifecycleStageDialogOpen] = useState(false);
  const [isSyncingOwnerToHubspot, setIsSyncingOwnerToHubspot] = useState(false);
  
  // Search states for dropdowns
  const [statusSearchQuery, setStatusSearchQuery] = useState('');
  const [lifecycleSearchQuery, setLifecycleSearchQuery] = useState('');
  
  // Lifecycle stage selection state (for two-step selection with reason)
  const [selectedLifecycleForReason, setSelectedLifecycleForReason] = useState(null);

  // Form states
  const [newNote, setNewNote] = useState('');
  const [newWhatsappMessage, setNewWhatsappMessage] = useState('');
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '', 
    due_date: '', 
    priority: 'medium',
    reminder_enabled: false,
    reminder_time: '30', // minutes before
    notification_enabled: false
  });
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const [playingRecording, setPlayingRecording] = useState(null);
  const audioRef = useRef(null);
  
  // Additional states from old design
  const [auditLog, setAuditLog] = useState([]);
  const [userPhone, setUserPhone] = useState('');
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [isCalComDialogOpen, setIsCalComDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: '', description: '', start_time: '', duration_minutes: 30, meeting_type: 'call', location: '' });
  const [isResearching, setIsResearching] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState(null);
  
  // Timeline "Read more" expanded state
  const [expandedTimelineItems, setExpandedTimelineItems] = useState({});
  
  // Post-call disposition modal state
  const [isPostCallDialogOpen, setIsPostCallDialogOpen] = useState(false);
  const [postCallData, setPostCallData] = useState({
    call_id: null,
    note: '',
    duration: null
  });

  // Toggle timeline item expansion
  const toggleTimelineExpand = (itemId) => {
    setExpandedTimelineItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Fetch all data
  useEffect(() => {
    if (!token || !contactId) return;
    fetchContact();
    fetchActivities();
    fetchTasks();
    fetchMeetings();
    fetchEmails();
    fetchCalls();
    fetchWhatsappMessages();
    fetchDeals();
    fetchAllUsers();
    fetchLeadStatuses();
    fetchLifecycleStages();
    fetchHubspotSyncInfo();
    fetchAuditLog();
    fetchUserPhone();
    fetchCustomFields();
    fetchAvailableTags();
  }, [contactId, token]);

  // Auto-refresh WhatsApp messages
  useEffect(() => {
    let interval;
    if (activeTab === 'whatsapp') {
      interval = setInterval(fetchWhatsappMessages, 10000);
    }
    return () => interval && clearInterval(interval);
  }, [activeTab, contactId]);

  // Auto-refresh Calls when on calls tab
  useEffect(() => {
    let interval;
    if (activeTab === 'calls') {
      fetchCalls(); // Initial fetch when tab is opened
      interval = setInterval(fetchCalls, 15000); // Refresh every 15 seconds
    }
    return () => interval && clearInterval(interval);
  }, [activeTab, contactId]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contacts/${contactId}?token=${token}`);
      setContact(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Failed to fetch contact', error);
      toast.error('Failed to load contact');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/activities?token=${token}&limit=100`);
      // Handle both array and object response formats
      const activitiesData = Array.isArray(response.data) ? response.data : (response.data?.activities || []);
      // Sort newest-first so the most recent activity appears at the
      // top of the All Activities tab. Backend ordering is
      // inconsistent across endpoints (some natural insert order,
      // some not), so we apply a deterministic descending sort here
      // by whichever timestamp field is populated — ``timestamp`` is
      // the standard column, ``created_at`` the fallback for older
      // rows. Tied / missing dates fall to the bottom.
      const sortedActivities = [...activitiesData].sort((a, b) => {
        const ta = new Date(a.timestamp || a.created_at || 0).getTime() || 0;
        const tb = new Date(b.timestamp || b.created_at || 0).getTime() || 0;
        return tb - ta;
      });
      setActivities(sortedActivities);
      // Filter notes - check both 'type' and 'activity_type' fields since backend uses 'activity_type'
      setNotes(sortedActivities.filter(a =>
        a.activity_type === 'note' || a.activity_type === 'note_added' ||
        a.type === 'note' || a.type === 'note_added'
      ));
    } catch (error) {
      console.error('Failed to fetch activities', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/tasks?token=${token}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks', error);
    }
  };

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/meetings?token=${token}`);
      setMeetings(response.data);
    } catch (error) {
      console.error('Failed to fetch meetings', error);
    }
  };

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/email/messages/${contactId}?token=${token}`);
      setEmails(response.data?.emails || []);
    } catch (error) {
      setEmails([]);
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/calls/history?token=${token}&contact_id=${contactId}`);
      setCalls(response.data?.calls || []);
    } catch (error) {
      setCalls([]);
    }
  };

  const fetchWhatsappMessages = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages/${contactId}?token=${token}`);
      setWhatsappMessages(response.data?.messages || []);
    } catch (error) {
      setWhatsappMessages([]);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals?token=${token}&contact_id=${contactId}`);
      setDeals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDeals([]);
    }
  };

  const fetchAllUsers = async () => {
    if (!token) return;
    setIsLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/accounts/users?token=${token}`);
      // API returns {success: true, users: [...]} so extract the users array
      const users = response.data?.users || response.data || [];
      setAllUsers(users);
      console.log('[fetchAllUsers] Loaded users:', users.length);
    } catch (error) {
      console.error('[fetchAllUsers] Error:', error);
      setAllUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchLeadStatuses = async (dept = selectedDepartment) => {
    try {
      const response = await axios.get(`${API}/master/lead-statuses?token=${token}&department=${dept}`);
      if (response.data.success && response.data.statuses) {
        setLeadStatuses(response.data.statuses);
      } else {
        setLeadStatuses([]);
      }
    } catch (error) {
      console.error('Failed to fetch lead statuses:', error);
      setLeadStatuses([]);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/audit-log?token=${token}`);
      setAuditLog(response.data || []);
    } catch (error) {
      setAuditLog([]);
    }
  };

  const fetchLifecycleStages = async (dept = selectedDepartment) => {
    try {
      const response = await axios.get(`${API}/master/lifecycle-stages?token=${token}&department=${dept}`);
      if (response.data.success && response.data.stages && response.data.stages.length > 0) {
        setLifecycleStages(response.data.stages);
      } else {
        setLifecycleStages([]);
      }
    } catch (error) {
      console.error('Failed to fetch lifecycle stages:', error);
      setLifecycleStages([]);
    }
  };

  const fetchHubspotSyncInfo = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/hubspot-sync-info?token=${token}`);
      if (response.data?.hubspot_sync_info) {
        setHubspotSyncInfo(response.data.hubspot_sync_info);
      }
    } catch (error) {
      console.error('Failed to fetch HubSpot sync info', error);
    }
  };

  const syncOwnerToHubspot = async () => {
    try {
      setIsSyncingOwnerToHubspot(true);
      const response = await axios.post(`${API}/contacts/${contactId}/sync-hubspot-owner?token=${token}`);
      if (response.data?.success) {
        await fetchHubspotSyncInfo();
        toast.success('Owner synced to HubSpot successfully');
      } else {
        toast.error(response.data.message || 'Failed to sync owner');
      }
    } catch (error) {
      console.error('Failed to sync owner to HubSpot', error);
      toast.error(error.response?.data?.detail || 'Failed to sync owner to HubSpot');
    } finally {
      setIsSyncingOwnerToHubspot(false);
    }
  };

  const handleLifecycleStageChange = async (newStage) => {
    try {
      await axios.patch(`${API}/contacts/${contactId}?token=${token}`, { lifecycle_stage: newStage });
      setContact({ ...contact, lifecycle_stage: newStage });
      setIsLifecycleStageDialogOpen(false);
      toast.success('Lifecycle stage updated');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update lifecycle stage');
    }
  };

  const fetchUserPhone = async () => {
    try {
      const response = await axios.get(`${API}/calls/user-phone?token=${token}`);
      const phoneToUse = response.data?.twilioPhoneNumber || response.data?.phoneNumber || user?.phone;
      setUserPhone(phoneToUse);
    } catch (error) {
      setUserPhone(user?.mobile_number || user?.phone || '');
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API}/master/custom-fields?token=${token}&include_inactive=false`);
      setCustomFields(response.data.fields || []);
    } catch (error) {
      console.error('Failed to fetch custom fields', error);
    }
  };

  const fetchAvailableTags = async () => {
    try {
      const response = await axios.get(`${API}/master/tags?token=${token}&include_inactive=false`);
      setAvailableTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags', error);
    }
  };

  // Refetch statuses and lifecycle when department changes
  useEffect(() => {
    if (token) {
      fetchLeadStatuses(selectedDepartment);
      fetchLifecycleStages(selectedDepartment);
    }
  }, [selectedDepartment, token]);

  // Update department when user loads/changes
  useEffect(() => {
    if (user?.email) {
      const dept = user.email.toLowerCase() === 'z.kheldoune@fakhruddinproperties.com' ? 'telesales' : 'sales';
      setSelectedDepartment(dept);
    }
  }, [user?.email]);

  // Handlers
  const handleSaveContact = async () => {
    try {
      await axios.patch(`${API}/contacts/${contactId}?token=${token}`, editData);
      setContact({ ...contact, ...editData });
      setIsEditing(false);
      toast.success('Contact updated');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/contacts/${contactId}?token=${token}`, { status: newStatus });
      setContact({ ...contact, status: newStatus });
      setIsStatusDialogOpen(false);
      toast.success('Status updated');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleOwnerChange = async (newOwnerId) => {
    try {
      const response = await axios.patch(`${API}/contacts/${contactId}?token=${token}`, { owner_id: newOwnerId });
      console.log('[OWNER_CHANGE] Success:', response.data);
      fetchContact();
      setIsOwnerDialogOpen(false);
      toast.success('Owner updated');
    } catch (error) {
      console.error('[OWNER_CHANGE] Error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to update owner';
      toast.error(errorMsg);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/contacts/${contactId}/notes?token=${token}`, { content: newNote });
      setNewNote('');
      setIsNoteDialogOpen(false);
      fetchActivities();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      await axios.post(`${API}/contacts/${contactId}/tasks?token=${token}`, newTask);
      
      // Play notification sound if notification is enabled
      if (newTask.notification_enabled) {
        import('@/utils/notificationSound').then(({ default: notificationSound }) => {
          notificationSound.playNotificationSound('task');
        });
        toast.success('Task created with notification enabled');
      } else {
        toast.success('Task created');
      }
      
      setNewTask({ 
        title: '', 
        description: '', 
        due_date: '', 
        priority: 'medium',
        reminder_enabled: false,
        reminder_time: '30',
        notification_enabled: false
      });
      setIsTaskDialogOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleSendWhatsApp = async () => {
    if (!newWhatsappMessage.trim() || !contact?.phone) return;
    try {
      setSendingWhatsapp(true);
      // Use the contact-specific endpoint that properly stores messages
      await axios.post(`${API}/whatsapp/send-to-contact/${contactId}?token=${token}`, {
        message: newWhatsappMessage
      });
      setNewWhatsappMessage('');
      fetchWhatsappMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingWhatsapp(false);
    }
  };

  // Tag handlers
  const handleAddTag = async (tag) => {
    try {
      setAddingTag(true);
      await axios.post(`${API}/master/contacts/${contactId}/tags?token=${token}`, {
        tag_name: tag.name
      });
      // Update local contact tags
      const newTags = [...(contact.tags || []), tag.name];
      setContact({ ...contact, tags: newTags });
      toast.success(`Tag "${tag.name}" added`);
    } catch (error) {
      toast.error('Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleRemoveTag = async (tagName) => {
    try {
      await axios.delete(`${API}/master/contacts/${contactId}/tags/${encodeURIComponent(tagName)}?token=${token}`);
      // Update local contact tags
      const newTags = (contact.tags || []).filter(t => t !== tagName);
      setContact({ ...contact, tags: newTags });
      toast.success(`Tag "${tagName}" removed`);
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  // Filter available tags based on search and exclude already added tags
  const filteredAvailableTags = availableTags.filter(tag => {
    const alreadyAdded = (contact?.tags || []).includes(tag.name);
    const matchesSearch = !tagSearchQuery || tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase());
    return !alreadyAdded && matchesSearch && tag.is_active !== false;
  });

  // Open call dialog (not directly initiate call)
  const initiateCall = async () => {
    if (!contact?.phone) {
      toast.error('Contact has no phone number');
      return;
    }
    // Open dialog to confirm/edit phone number
    setIsCallDialogOpen(true);
  };

  const toggleRecordingPlayback = (call) => {
    if (playingRecording === call.id) {
      audioRef.current?.pause();
      setPlayingRecording(null);
    } else {
      setPlayingRecording(call.id);
      if (audioRef.current) {
        audioRef.current.src = call.recording_url;
        audioRef.current.play();
      }
    }
  };

  // Helpers
  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (email) return email[0].toUpperCase();
    return '?';
  };

  const getStatusStyle = (status) => statusStyles[status] || statusStyles.new;

  const getLeadScore = () => contact?.lead_score || 50;

  const getPriorityLabel = (score) => {
    if (score >= 80) return { label: 'Hot', color: 'text-red-600 bg-red-50' };
    if (score >= 60) return { label: 'Warm', color: 'text-amber-600 bg-amber-50' };
    return { label: 'Cold', color: 'text-blue-600 bg-blue-50' };
  };

  // Call status badge helper
  const getCallStatusBadge = (status) => {
    const statusConfig = {
      initiating: { label: 'Initiating', color: 'bg-yellow-100 text-yellow-800' },
      calling_user: { label: 'Calling You', color: 'bg-blue-100 text-blue-800' },
      user_answered: { label: 'Connecting...', color: 'bg-blue-100 text-blue-800' },
      calling_contact: { label: 'Dialing Contact', color: 'bg-purple-100 text-purple-800' },
      connected: { label: 'Connected', color: 'bg-green-100 text-green-800' },
      completed: { label: 'Completed', color: 'bg-slate-100 text-slate-800' },
      failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
      no_answer: { label: 'No Answer', color: 'bg-orange-100 text-orange-800' },
      busy: { label: 'Busy', color: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Get playable recording URL (use proxy for Twilio URLs)
  const getPlayableRecordingUrl = (callId) => {
    return `${API}/calls/recording/${callId}?token=${token}`;
  };

  // Handle call with phone dialog
  const handleCallWithPhoneDialog = () => {
    if (!contact?.phone) {
      toast.error('Contact has no phone number');
      return;
    }
    setIsCallDialogOpen(true);
  };

  // Initiate call with specific phone
  const initiateCallWithPhone = async (phone) => {
    if (!contact?.phone) return;
    try {
      setIsInitiatingCall(true);
      const response = await axios.post(`${API}/calls/initiate?token=${token}`, {
        contact_id: contactId,
        to_number: contact.phone,
        from_number: phone || userPhone
      });
      if (response.data?.success || response.data?.call_id) {
        toast.success('Calling your phone now. Please answer to connect to the contact.');
        setIsCallDialogOpen(false);
        setTimeout(() => fetchCalls(), 2000);
      } else {
        toast.error(response.data?.error || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Failed to initiate call', error);
      toast.error('Failed to initiate call');
    } finally {
      setIsInitiatingCall(false);
    }
  };

  // Handle call through (native dialer)
  const handleCallThrough = async () => {
    const phoneNumber = contact?.phone;
    if (!phoneNumber) {
      toast.error('Contact has no phone number');
      return;
    }
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    try {
      // Log the call-through activity
      await axios.post(`${API}/activities/call-through?token=${token}`, {
        contact_id: contactId,
        phone_number: cleanPhone
      });
      window.location.href = `tel:${cleanPhone}`;
      toast.success('Opening phone dialer');
      
      // Show post-call disposition dialog after a delay (giving time for the call)
      // The user can also manually trigger this via the "Log Call" button
      setTimeout(() => {
        triggerPostCallAlert();
      }, 5000); // Show after 5 seconds
    } catch (error) {
      console.error('Failed to log call through:', error);
      window.location.href = `tel:${cleanPhone}`;
    }
  };

  // Post-call disposition alert with sound
  const triggerPostCallAlert = () => {
    // Play notification sound
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Sound notification not supported');
    }
    
    // Show the post-call dialog
    setPostCallData({ call_id: null, note: '', duration: null });
    setIsPostCallDialogOpen(true);
    toast.info('Please update lead status and add call notes', { duration: 5000 });
  };

  // Handle post-call disposition save
  const handlePostCallSave = async () => {
    try {
      // Add note if provided
      if (postCallData.note.trim()) {
        await axios.post(`${API}/contacts/${contactId}/notes?token=${token}`, {
          content: `📞 Call Note: ${postCallData.note}`,
          note_type: 'call_note'
        });
        fetchActivities();
      }
      
      setIsPostCallDialogOpen(false);
      setPostCallData({ call_id: null, note: '', duration: null });
      toast.success('Call disposition saved');
    } catch (error) {
      console.error('Failed to save call disposition:', error);
      toast.error('Failed to save call note');
    }
  };

  // AI Research function
  const handleResearch = async () => {
    try {
      setIsResearching(true);
      const response = await axios.post(`${API}/ai/research-lead?token=${token}`, {
        contact_id: contactId
      });
      setEnrichmentData(response.data);
      toast.success('Research completed');
    } catch (error) {
      toast.error('Research failed');
    } finally {
      setIsResearching(false);
    }
  };

  // Create meeting
  const handleCreateMeeting = async () => {
    try {
      await axios.post(`${API}/contacts/${contactId}/meetings?token=${token}`, newMeeting);
      setIsMeetingDialogOpen(false);
      setNewMeeting({ title: '', description: '', start_time: '', duration_minutes: 30, meeting_type: 'call', location: '' });
      fetchMeetings();
      toast.success('Meeting scheduled');
    } catch (error) {
      toast.error('Failed to create meeting');
    }
  };

  // Complete task
  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete?token=${token}`);
      fetchTasks();
      fetchActivities();
      toast.success('Task completed');
    } catch (error) {
      console.error('Failed to complete task', error);
      toast.error('Failed to complete task');
    }
  };

  // Get lead statuses (reasons) for a specific lifecycle stage
  const getReasonsForLifecycle = (lifecycleStageName) => {
    if (!lifecycleStageName) return [];
    return leadStatuses.filter(status => 
      status.lifecycle_stage === lifecycleStageName
    );
  };

  // Handle lifecycle stage selection with reason
  const handleLifecycleWithReason = async (lifecycleStage, reason) => {
    try {
      // Update both lifecycle stage and lead status
      await axios.patch(`${API}/contacts/${contactId}?token=${token}`, {
        lifecycle_stage: lifecycleStage,
        status: reason
      });
      setContact({ ...contact, lifecycle_stage: lifecycleStage, status: reason });
      setIsLifecycleStageDialogOpen(false);
      setSelectedLifecycleForReason(null);
      setLifecycleSearchQuery('');
      fetchActivities();
      toast.success(`Updated to ${lifecycleStage} - ${reason}`);
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Helper to get display label for lead status (hides IDs, shows only label)
  const getStatusDisplayLabel = (statusValue) => {
    if (!statusValue) return 'New';
    // Find the status in leadStatuses array
    const found = leadStatuses.find(s => 
      s.value === statusValue || s.name === statusValue || s.id === statusValue
    );
    if (found) {
      return found.name || found.display_name || found.label || statusValue;
    }
    // Fallback: format the value nicely
    return statusValue.toString().replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  };

  // Helper to get display label for lifecycle stage (hides IDs, shows only label)
  const getLifecycleDisplayLabel = (stageValue) => {
    if (!stageValue) return 'Lead';
    // Find the stage in lifecycleStages array
    const found = lifecycleStages.find(s => 
      s.name === stageValue || s.value === stageValue || s.id === stageValue
    );
    if (found) {
      return found.display_name || found.name || found.label || stageValue;
    }
    // Fallback: format the value nicely
    return stageValue.toString().replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  };

  // Filter lead statuses based on search query
  const filteredLeadStatuses = (leadStatuses.length > 0 ? leadStatuses : [
    { name: 'New', value: 'new' },
    { name: 'Contacted', value: 'contacted' },
    { name: 'Qualified', value: 'qualified' },
    { name: 'Opportunity', value: 'opportunity' },
    { name: 'Customer', value: 'customer' },
    { name: 'Not Interested', value: 'not_interested' },
    { name: 'Lost', value: 'lost' }
  ]).filter(status => {
    if (!statusSearchQuery) return true;
    const label = (status.name || status.display_name || status.label || '').toLowerCase();
    return label.includes(statusSearchQuery.toLowerCase());
  });

  // Filter lifecycle stages based on search query
  const filteredLifecycleStages = lifecycleStages.filter(stage => {
    if (!lifecycleSearchQuery) return true;
    const label = (stage.display_name || stage.name || stage.label || '').toLowerCase();
    return label.includes(lifecycleSearchQuery.toLowerCase());
  });

  // Filter function to determine if an activity should be shown in timeline
  const shouldShowActivity = (activity) => {
    const type = (activity.type || activity.activity_type || '').toLowerCase();
    const title = (activity.title || '').toLowerCase();
    const user = (activity.performed_by_name || '').toLowerCase();
    const description = (activity.description || activity.content || '').toLowerCase();
    const isAutomated = activity.is_automated === true;
    
    // System-generated types to always hide (noise/background processes)
    const alwaysHiddenTypes = [
      'hubspot_owner_sync',
      'owner_sync',
      'sync_completed',
      'lead_returned_to_actionable',
      'orchestration_status_update',
      'orchestration_assignment',
      'system_update',
      'auto_status_change',
      'missing_mobile_number',
      'validation_check',
      'data_quality_check',
      'background_job'
    ];
    
    if (alwaysHiddenTypes.includes(type)) {
      return false;
    }
    
    // Hide "Missing Mobile Number Detected" and similar system alerts
    if (title.includes('missing') && title.includes('detected')) {
      return false;
    }
    
    // Check if it's a system-generated message based on performer
    const isSystemPerformer = 
      user === 'system' ||
      user.includes('orchestrator') ||
      user.includes('lead management') ||
      user.includes('assignment orchestrator') ||
      user.includes('hubspot sync') ||
      user.includes('automation') ||
      user.includes('workflow automation');
    
    // Generic/redundant titles to hide
    const genericTitles = [
      'status changed',
      'status updated',
      'note updated',
      'owner changed',
      'field updated',
      'record updated',
      'lead returned to actionable',
      'lead returned',
      'contact updated',
      'data synced'
    ];
    
    const hasGenericTitle = genericTitles.some(gt => title.includes(gt));
    
    // Hide system messages with generic titles
    if (isSystemPerformer && hasGenericTitle) {
      return false;
    }
    
    // Hide purely automated entries with no meaningful description
    if (isAutomated && !description && hasGenericTitle) {
      return false;
    }
    
    // Hide sync/orchestration activities unless they have meaningful content
    if (type.includes('orchestration') || type.includes('sync')) {
      if (!description || description.length < 20) {
        return false;
      }
    }
    
    // Always show these user-meaningful action types
    const meaningfulActionTypes = [
      'note_added', 'note', 'task_created', 'task', 
      'call_made', 'call_received', 'call', 'ai_call', 'ai_call_completed',
      'email_sent', 'email_received', 'email',
      'meeting_scheduled', 'meeting_completed', 'meeting',
      'whatsapp_sent', 'whatsapp_received', 'whatsapp',
      'lead_captured', 'deal_created', 'deal_updated',
      'call_through', 'sms_sent', 'sms_received'
    ];
    
    if (meaningfulActionTypes.includes(type)) {
      return true;
    }
    
    // Show owner/status changes ONLY if done by a real user
    if ((type === 'owner_changed' || type === 'status_changed' || type === 'lead_reassignment') && !isSystemPerformer) {
      return true;
    }
    
    // For other entries: show only if not system-generated OR has substantial content
    if (isSystemPerformer) {
      // Only show system entries if they have meaningful, lengthy descriptions
      return description && description.length > 50;
    }
    
    // Show user-performed actions with any content
    return !!description || !hasGenericTitle;
  };

  // Deduplicate related events within a short time window
  const deduplicateEvents = (events) => {
    const deduped = [];
    const seen = new Map(); // Track events by type + approximate time
    
    for (const event of events) {
      // Create a key based on type and time window (5 minute window)
      const timeKey = Math.floor(event.date.getTime() / (5 * 60 * 1000));
      const key = `${event.type}-${timeKey}`;
      
      // For certain types, prefer the one with more description
      if (seen.has(key)) {
        const existing = seen.get(key);
        // Keep the one with more content
        if ((event.description?.length || 0) > (existing.description?.length || 0)) {
          // Remove old, add new
          const idx = deduped.indexOf(existing);
          if (idx > -1) deduped.splice(idx, 1);
          deduped.push(event);
          seen.set(key, event);
        }
      } else {
        deduped.push(event);
        seen.set(key, event);
      }
    }
    
    return deduped;
  };

  // Build unified timeline
  const buildTimeline = () => {
    const events = [];
    
    // Add lead creation event with UTM attribution
    if (contact?.created_at) {
      const utmData = {
        utm_source: contact.utm_source,
        utm_medium: contact.utm_medium,
        utm_campaign: contact.utm_campaign,
        campaign_id: contact.campaign_id,
        adset_name: contact.adset_name,
        ad_name: contact.ad_name
      };
      const hasUtm = Object.values(utmData).some(v => v);
      
      events.push({
        id: 'lead_created',
        type: 'lead_created',
        date: toUTCDate(contact.created_at),
        title: 'Lead Created',
        description: contact.source ? `Source: ${contact.source}` : 'New lead added',
        user: contact.created_by_name || 'System',
        source: 'system',
        utm: hasUtm ? utmData : null
      });
    }

    // Add status changes if available
    if (contact?.status_history?.length > 0) {
      contact.status_history.forEach(change => {
        events.push({
          id: `status_${change.timestamp}`,
          type: 'status_change',
          date: toUTCDate(change.timestamp || change.changed_at),
          title: 'Status Changed',
          description: `${change.from_status || 'Unknown'} → ${change.to_status || change.status}`,
          user: change.changed_by_name || change.user,
          source: 'system'
        });
      });
    }

    // Add assignment changes
    if (contact?.assignment_history?.length > 0) {
      contact.assignment_history.forEach(assignment => {
        events.push({
          id: `assignment_${assignment.timestamp}`,
          type: 'assignment',
          date: toUTCDate(assignment.timestamp || assignment.assigned_at),
          title: 'Lead Reassigned',
          description: `Assigned to ${assignment.assigned_to_name || 'team member'}`,
          user: assignment.assigned_by_name || 'System',
          source: 'system'
        });
      });
    }
    
    // Filter activities to only show meaningful ones
    const filteredActivities = activities.filter(shouldShowActivity);
    
    // Every event gets a `user` value. Falls back to 'System' for
    // automated entries (rotation, SLA, lifecycle changes etc.) so
    // the "by X" line under the title is never blank — the timeline
    // always names the actor, human or otherwise.
    filteredActivities.forEach(a => events.push({
      id: a.id, type: a.type || a.activity_type || 'activity',
      date: toUTCDate(a.timestamp || a.created_at),
      title: a.title || a.type?.replace(/_/g, ' '),
      description: a.description || a.content,
      user: a.performed_by_name || (a.is_automated ? 'System' : 'System'),
      source: 'activity',
      isAutomated: a.is_automated,
      metadata: a.metadata
    }));

    calls.forEach(c => events.push({
      id: c.id, type: 'call', date: toUTCDate(c.created_at || c.start_time),
      title: `Call ${c.direction === 'inbound' ? 'Received' : 'Made'}`,
      description: c.status === 'completed' ? `Duration: ${formatCallDuration(c.duration_seconds)}` : c.status,
      user: c.user_name || 'Phone system', metadata: c, source: 'call',
      outcome: c.outcome || c.status
    }));

    whatsappMessages.forEach(m => events.push({
      id: m.id, type: m.direction === 'outgoing' ? 'whatsapp_out' : 'whatsapp_in',
      date: toUTCDate(m.timestamp || m.created_at),
      title: m.direction === 'outgoing' ? 'WhatsApp Sent' : 'WhatsApp Received',
      description: m.message || m.text || m.body,
      user: m.direction === 'outgoing'
        ? (user?.full_name || 'You')
        : (contact?.name || 'Client'),
      source: 'whatsapp'
    }));

    emails.forEach(e => events.push({
      id: e.id, type: e.direction === 'received' ? 'email_in' : 'email_out',
      date: toUTCDate(e.sent_at || e.received_at || e.created_at),
      title: e.direction === 'received' ? 'Email Received' : 'Email Sent',
      description: e.subject,
      user: e.direction === 'received' ? (e.from || 'Client') : (user?.full_name || 'You'),
      source: 'email',
      opened: e.opened_at ? true : false,
      replied: e.replied_at ? true : false
    }));

    meetings.forEach(m => events.push({
      id: m.id, type: 'meeting', date: toUTCDate(m.start_time || m.created_at),
      title: m.title || 'Meeting', description: `${m.duration_minutes || 30} min - ${m.meeting_type}`,
      user: m.organizer_name || 'System', source: 'meeting',
      status: m.status // scheduled, completed, no-show
    }));

    tasks.forEach(t => events.push({
      id: t.id, type: 'task', date: toUTCDate(t.created_at),
      title: t.title, description: t.description,
      user: t.assigned_to_name || t.created_by_name || 'System',
      metadata: t, source: 'task',
      status: t.status, // pending, completed, overdue
      dueDate: t.due_date
    }));

    // Sort by date descending, then deduplicate
    const sorted = events.sort((a, b) => b.date - a.date);
    return deduplicateEvents(sorted);
  };

  const timeline = buildTimeline();

  // Event icon mapping. The backend lumps a lot of stuff under
  // activity_type='ai_call' regardless of outcome, so we also
  // sniff the title for failure / no-answer / completion markers.
  // Without that, every AI-call row showed a generic grey Activity
  // icon instead of differentiating failed (red) from completed.
  const TITLE_FAILURE_HINTS = ['failed', 'error', 'breach', 'missed'];
  const TITLE_PENDING_HINTS = ['no answer', 'no-answer', 'voicemail', 'busy'];
  const TITLE_SUCCESS_HINTS = ['completed', 'connected', 'answered'];
  const TITLE_TRIGGER_HINTS = ['actionable', 'triggered', 'queued', 'auto-assigned'];

  const titleHas = (title, hints) =>
    !!title && hints.some(h => title.toLowerCase().includes(h));

  const getEventIcon = (event) => {
    // Backwards compat: this function used to take a string type.
    const type = typeof event === 'string' ? event : event?.type;
    const title = typeof event === 'string' ? '' : (event?.title || '');

    // Failure overrides anything else — red AlertCircle.
    if (titleHas(title, TITLE_FAILURE_HINTS)) return AlertCircle;
    if (titleHas(title, TITLE_PENDING_HINTS)) return PhoneIncoming;
    if (titleHas(title, TITLE_TRIGGER_HINTS)) return Zap;

    const icons = {
      call: PhoneCall, whatsapp_out: MessageCircle, whatsapp_in: MessageCircle,
      email_out: Mail, email_in: MailOpen, meeting: Calendar, task: CheckSquare,
      note: FileText, note_added: FileText, status_change: RefreshCw,
      lead_created: UserPlus, ai_call_completed: Bot, assignment: User,
      deal_created: Briefcase, deal_stage_change: TrendingUp,
      ai_call: titleHas(title, TITLE_SUCCESS_HINTS) ? Bot : PhoneOutgoing,
      ai_call_initiated: PhoneOutgoing, ai_call_failed: AlertCircle,
      ai_call_no_answer: PhoneIncoming,
      lead_actionable: Zap, lead_marked_actionable: Zap,
      workflow_trigger: Zap, lead_lifecycle_change: RefreshCw,
      sla_warning: Bell, sla_breach: AlertCircle,
      contact_created: UserPlus,
    };
    return icons[type] || Activity;
  };

  const getEventColor = (event) => {
    const type = typeof event === 'string' ? event : event?.type;
    const title = typeof event === 'string' ? '' : (event?.title || '');

    if (titleHas(title, TITLE_FAILURE_HINTS)) return 'bg-red-500';
    if (titleHas(title, TITLE_PENDING_HINTS)) return 'bg-amber-500';
    if (titleHas(title, TITLE_TRIGGER_HINTS)) return 'bg-yellow-500';

    const colors = {
      call: 'bg-blue-500', whatsapp_out: 'bg-green-500', whatsapp_in: 'bg-green-500',
      email_out: 'bg-purple-500', email_in: 'bg-purple-400', meeting: 'bg-orange-500',
      task: 'bg-emerald-500', note: 'bg-amber-500', note_added: 'bg-amber-500',
      status_change: 'bg-indigo-500', lead_created: 'bg-teal-500', ai_call_completed: 'bg-pink-500',
      assignment: 'bg-cyan-500', deal_created: 'bg-rose-500', deal_stage_change: 'bg-violet-500',
      ai_call: titleHas(title, TITLE_SUCCESS_HINTS) ? 'bg-pink-500' : 'bg-blue-500',
      ai_call_initiated: 'bg-blue-500', ai_call_failed: 'bg-red-500',
      ai_call_no_answer: 'bg-amber-500',
      lead_actionable: 'bg-yellow-500', lead_marked_actionable: 'bg-yellow-500',
      workflow_trigger: 'bg-yellow-500', lead_lifecycle_change: 'bg-indigo-500',
      sla_warning: 'bg-amber-500', sla_breach: 'bg-red-500',
      contact_created: 'bg-teal-500',
    };
    return colors[type] || 'bg-slate-500';
  };

  // Parse "Key: Value" lines out of a timeline description so the
  // renderer can show them as small label + badge chips instead of
  // plain text. Lines that don't match the pattern fall through as
  // regular text. Keeps the visual hierarchy clean — readers scan
  // the chips for facts and read the sentences for context.
  const parseDescriptionLines = (text) => {
    if (!text || typeof text !== 'string') return [];
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    return lines.map((line, idx) => {
      // Match "Label: Value" where Label is short, alphabetic,
      // capitalised. Avoids false-positives on free-form sentences
      // that happen to contain a colon ("Failed to call: blah").
      const m = line.match(/^([A-Z][A-Za-z ]{1,24}):\s+(.+)$/);
      if (m && !m[2].includes(':')) {
        return { key: idx, type: 'chip', label: m[1], value: m[2] };
      }
      return { key: idx, type: 'text', text: line };
    });
  };

  // Pretty-print a JSON string. Falls back to the raw text if it
  // isn't valid JSON so we never break the "View details" panel
  // when the backend dumps a non-JSON tail.
  //
  // Real-world quirk: backend error messages are often truncated
  // mid-JSON (e.g. f"...:{error_text[:200]}" cuts off the closing
  // braces). Direct JSON.parse fails. We do a brace-balance pass —
  // count unclosed `{` / `[` outside strings, append matching
  // closers, and try parse again. If THAT still fails, return raw.
  const prettyPrintMaybeJson = (raw) => {
    if (!raw || typeof raw !== 'string') return raw || '';
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return raw;

    const tryParse = (s) => {
      try { return JSON.stringify(JSON.parse(s), null, 2); }
      catch { return null; }
    };

    const direct = tryParse(trimmed);
    if (direct) return direct;

    // Walk the string and track open braces/brackets outside of
    // string literals. Append the right number of closing tokens
    // to balance and retry.
    let openBrace = 0, openBracket = 0;
    let inStr = false, escape = false;
    for (const ch of trimmed) {
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inStr = !inStr; continue; }
      if (inStr) continue;
      if (ch === '{') openBrace++;
      else if (ch === '}') openBrace--;
      else if (ch === '[') openBracket++;
      else if (ch === ']') openBracket--;
    }
    // Truncated inside a string literal — we can't safely close it
    if (inStr) return raw;
    if (openBrace <= 0 && openBracket <= 0) return raw;

    const balanced =
      trimmed
      + ']'.repeat(Math.max(0, openBracket))
      + '}'.repeat(Math.max(0, openBrace));
    return tryParse(balanced) || raw;
  };

  // Some timeline rows arrive with a JSON blob appended to their
  // description (e.g. AI call failures dump the upstream provider
  // error). Showing raw JSON in the bubble is hostile — extract the
  // human prefix and keep the JSON behind a "View details" toggle.
  const formatTimelineDescription = (text) => {
    if (!text || typeof text !== 'string') {
      return { friendly: text || '', details: null };
    }
    // Detect a JSON object starting somewhere in the body, anchored
    // at the first '{' after any human-readable lead-in.
    const jsonStart = text.search(/[:\s]\{["a-zA-Z_]/);
    if (jsonStart === -1) {
      return { friendly: text, details: null };
    }
    // Pull friendly part up to the marker, trim trailing colon/space
    const friendly = text.slice(0, jsonStart).replace(/[:\s]+$/, '').trim();
    const details = text.slice(jsonStart).replace(/^[:\s]+/, '').trim();
    // If the friendly portion is empty or too short, fall back to
    // the whole thing so we don't hide a useful one-liner.
    if (!friendly || friendly.length < 6) {
      return { friendly: text, details: null };
    }
    return { friendly, details };
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="animate-spin text-slate-400" size={32} />
          <span className="text-slate-500 text-sm">Loading contact...</span>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AlertCircle size={48} className="text-slate-300" />
        <p className="text-slate-500">Contact not found</p>
        <Button variant="outline" onClick={() => navigate('/contacts')}>
          <ArrowLeft size={16} className="mr-2" /> Back to Contacts
        </Button>
      </div>
    );
  }

  const statusStyle = getStatusStyle(contact.status);
  const priority = getPriorityLabel(getLeadScore());
  const pendingTasks = tasks.filter(t => t.status !== 'completed');
  const upcomingMeetings = meetings.filter(m => new Date(m.start_time) > new Date());

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-detail-page">
      {/* Hidden audio element for call recordings */}
      <audio ref={audioRef} onEnded={() => setPlayingRecording(null)} />

      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-[1800px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} className="gap-2 text-slate-600">
              <ArrowLeft size={16} /> Contacts
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8 border border-slate-200">
                <AvatarFallback className="bg-slate-900 text-white text-xs font-medium">
                  {getInitials(contact.name, contact.email)}
                </AvatarFallback>
              </Avatar>
              <span className="font-semibold text-slate-900">{contact.name || contact.email?.split('@')[0] || 'Unknown'}</span>
              <Badge className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} text-xs font-medium`}>
                {getStatusDisplayLabel(contact.status)}
              </Badge>
            </div>
          </div>
          
          {/* Quick Actions — compact toolbar
              Primary 4 visible (Call, WhatsApp, Email, Edit). Everything
              else (Call Through, Log Call, Task, Schedule, Meeting)
              lives under a single "More" dropdown so the header stays
              scannable on smaller screens. */}
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={initiateCall}
              disabled={isInitiatingCall || !contact.phone}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white h-8"
              data-testid="quick-call-btn"
            >
              {isInitiatingCall ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
              Call
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setActiveTab('whatsapp')}
              disabled={!contact.phone}
              className="gap-1.5 border-green-200 text-green-700 hover:bg-green-50 h-8"
              data-testid="quick-whatsapp-btn"
            >
              <WhatsAppIcon size={14} />
              WhatsApp
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEmailDialogOpen(true)}
              disabled={!contact.email}
              className="gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50 h-8"
              data-testid="quick-email-btn"
            >
              <Mail size={14} />
              Email
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 h-8"
                  data-testid="quick-more-btn"
                >
                  <MoreHorizontal size={14} />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onClick={handleCallThrough}
                  disabled={!contact.phone}
                  data-testid="more-call-through"
                >
                  <PhoneOutgoing size={14} className="mr-2 text-teal-600" />
                  Call Through
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={triggerPostCallAlert}
                  data-testid="more-log-call"
                >
                  <FileText size={14} className="mr-2 text-amber-600" />
                  Log Call
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsSmsDialogOpen(true)}
                  disabled={!contact.phone}
                >
                  <MessageSquare size={14} className="mr-2 text-amber-600" />
                  Send SMS
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setIsTaskDialogOpen(true)}
                  data-testid="more-task"
                >
                  <CheckSquare size={14} className="mr-2 text-slate-600" />
                  New Task
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsCalComDialogOpen(true)}
                  data-testid="more-calcom"
                >
                  <CalendarCheck size={14} className="mr-2 text-orange-600" />
                  Schedule (Cal.com)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsMeetingDialogOpen(true)}
                  data-testid="more-meeting"
                >
                  <Calendar size={14} className="mr-2 text-slate-600" />
                  New Meeting
                </DropdownMenuItem>
                {/* "Request Approval" is hidden for CEO / owner /
                    account_owner — those roles are at the top of the
                    approval chain and decide, not file. Everyone
                    below them can submit. */}
                {!['ceo', 'owner', 'account_owner', 'super_admin'].some(
                  (h) => (user?.role || '').toLowerCase().includes(h)
                ) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setIsApprovalDialogOpen(true)}
                      data-testid="more-approval"
                    >
                      <ShieldCheck size={14} className="mr-2 text-violet-600" />
                      Request Approval
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Separator orientation="vertical" className="h-6 mx-1" />
            <Button
              size="sm"
              variant={isEditing ? 'default' : 'outline'}
              onClick={() => isEditing ? handleSaveContact() : setIsEditing(true)}
              className={`gap-1.5 h-8 ${isEditing ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              data-testid="edit-contact-btn"
            >
              {isEditing ? <><Save size={14} /> Save</> : <><Edit size={14} /> Edit</>}
            </Button>
            {isEditing && (
              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setIsEditing(false); setEditData(contact); }}>
                <X size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hero Card — full-width identity strip
          Replaces the role the Profile sidebar card used to play:
          large avatar, name + role + company, badges, and a row of
          at-a-glance KPI tiles (score, status, days in pipeline,
          source). Gives the page a clear, modern dashboard feel
          and frees the body grid to be a 2-column layout below. */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 pt-6">
        <Card className="border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              {/* Avatar + identity */}
              <div className="flex items-center gap-4 min-w-0">
                <Avatar className="h-16 w-16 ring-4 ring-white shadow-md flex-shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-900 text-white text-xl font-semibold">
                    {getInitials(contact.name, contact.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">
                    {contact.name || contact.email?.split('@')[0] || 'Unknown'}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-xs text-slate-500">
                    {contact.position && <span>{contact.position}</span>}
                    {contact.company && (
                      <span className="flex items-center gap-1">
                        <Building size={11} /> {contact.company}
                      </span>
                    )}
                    {contact.email && (
                      <span className="flex items-center gap-1 truncate max-w-[200px]">
                        <Mail size={11} /> {contact.email}
                      </span>
                    )}
                    {contact.phone && (
                      <span className="flex items-center gap-1 font-mono">
                        <Phone size={11} /> {contact.phone}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <Badge
                      className={`${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border} cursor-pointer text-[11px]`}
                      onClick={() => { setStatusSearchQuery(''); setIsStatusDialogOpen(true); }}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot} mr-1.5`} />
                      {getStatusDisplayLabel(contact.status)}
                    </Badge>
                    <Badge className={`${priority.color} text-[11px]`}>
                      <Star size={10} className="mr-1" fill="currentColor" />
                      {priority.label}
                    </Badge>
                  </div>
                  {/* "Lead Created From" breadcrumb — surfaces the
                      attribution chain (Source > Campaign > Ad Set >
                      Ad > Source ID) at a glance so marketing agents
                      don't have to open the Source Attribution card
                      to read it. Renders only when at least two
                      segments are present. */}
                  <LeadCreatedFromBreadcrumb contact={contact} />
                </div>
              </div>

              {/* KPI tiles — at-a-glance numbers
                  Lead Score (with progress hint), days in pipeline (from
                  created_at), last activity (relative), and the lead
                  source. Each tile is its own small card so the eye
                  picks them out without reading prose. */}
              <div className="sm:ml-auto grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 min-w-[100px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Score</p>
                  <p className="text-lg font-semibold text-slate-900 mt-0.5">{getLeadScore()}<span className="text-xs font-normal text-slate-400">/100</span></p>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 min-w-[100px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">In pipeline</p>
                  <p className="text-lg font-semibold text-slate-900 mt-0.5">
                    {(() => {
                      // Use toUTCDate so a naive backend timestamp
                      // isn't interpreted in the browser's local
                      // timezone — keeps the "days in pipeline"
                      // count consistent with the timeline's date
                      // rendering.
                      const created = toUTCDate(contact.created_at);
                      if (!created) return '—';
                      return `${Math.max(0, Math.floor((Date.now() - created.getTime()) / 86400000))}d`;
                    })()}
                  </p>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 min-w-[100px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Last activity</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">
                    {timeline.length > 0 ? formatRelativeTime(timeline[0].date) : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 min-w-[100px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Source</p>
                  <p className="text-sm font-semibold text-slate-900 mt-0.5 capitalize truncate">
                    {contact.source?.replace(/_/g, ' ') || 'Direct'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content - 2 Column Layout (Activity + Right Rail) */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">

          {/* RIGHT RAIL — on lg screens. Order-2 pushes it to the right.
              Collects what used to be in two separate side columns:
              SLA panel, Contact Info, Owner, Lead Details (top), then
              Pending Tasks + Meetings (below). One coherent column
              instead of two narrow stacks. */}
          <div className="col-span-12 lg:col-span-4 lg:order-2 space-y-4">
            {/* Profile card removed — its content (avatar, name,
                status, score) now lives in the full-width Hero card
                above. The 4-icon action grid is also redundant with
                the compact toolbar in the top nav, so we drop the
                whole panel and start the right rail directly with
                the SLA / Rotation summary. */}

            {/* SLA & Rotation panel — only renders for leads that have an
                SLA rule or any rotation history, so it stays invisible for
                everyday contacts and only appears where the timeline
                matters. */}
            <LeadRotationPanel contact={contact} />

            {/* Contact Information */}
            <Card className="p-5 border-slate-200" data-testid="contact-info-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <User size={14} className="text-slate-400" />
                  Contact Info
                </span>
                {!isEditing && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsEditing(true)}>
                    <Edit size={12} />
                  </Button>
                )}
              </h3>
              {/* In edit mode we render the full form so every field can
                  be set. In view mode we render only populated fields
                  and offer a row of "+ Add X" pills at the bottom for
                  whatever's missing — keeps the card from being a
                  forest of '-' placeholders on partially-filled leads. */}
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input
                      value={editData.email || ''}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Company</Label>
                    <Input
                      value={editData.company || ''}
                      onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Position/Title</Label>
                    <Input
                      value={editData.position || ''}
                      onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Address</Label>
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {contact.email && (
                      <div className="flex items-center gap-2 text-sm group">
                        <Mail size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700 truncate">{contact.email}</span>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100" onClick={() => navigator.clipboard.writeText(contact.email)}>
                          <Copy size={10} />
                        </Button>
                      </div>
                    )}
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-sm group">
                        <Phone size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700 font-mono">{contact.phone}</span>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100" onClick={() => navigator.clipboard.writeText(contact.phone)}>
                          <Copy size={10} />
                        </Button>
                      </div>
                    )}
                    {contact.company && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700">{contact.company}</span>
                      </div>
                    )}
                    {contact.position && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700">{contact.position}</span>
                      </div>
                    )}
                    {contact.address && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-700">{contact.address}</span>
                      </div>
                    )}
                    {contact.website && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe size={14} className="text-slate-400 flex-shrink-0" />
                        <a
                          href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline truncate max-w-[180px]"
                        >
                          {contact.website}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Target size={14} className="text-slate-400 flex-shrink-0" />
                      <Badge variant="outline" className="font-normal text-[11px]">
                        {contact.source?.replace(/_/g, ' ') || 'Direct'}
                      </Badge>
                    </div>
                  </div>

                  {/* "+ Add X" pills row — only fields that ARE missing
                      show up. Clicking any pill drops the user into edit
                      mode so the right input is ready to type into. */}
                  {(() => {
                    const missing = [
                      { key: 'phone', label: 'Phone', icon: Phone, present: !!contact.phone },
                      { key: 'email', label: 'Email', icon: Mail, present: !!contact.email },
                      { key: 'company', label: 'Company', icon: Building, present: !!contact.company },
                      { key: 'position', label: 'Title', icon: Briefcase, present: !!contact.position },
                      { key: 'address', label: 'Address', icon: MapPin, present: !!contact.address },
                      { key: 'website', label: 'Website', icon: Globe, present: !!contact.website },
                    ].filter(f => !f.present);
                    if (missing.length === 0) return null;
                    return (
                      <div className="mt-4 pt-3 border-t border-slate-100">
                        <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-2">Add details</p>
                        <div className="flex flex-wrap gap-1.5">
                          {missing.map(f => {
                            const FieldIcon = f.icon;
                            return (
                              <button
                                key={f.key}
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full px-2 py-1 transition-colors"
                                data-testid={`add-${f.key}-pill`}
                              >
                                <Plus size={10} />
                                <FieldIcon size={11} />
                                {f.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </Card>

            {/* Owner Card */}
            <Card className="p-5 border-slate-200" data-testid="owner-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
                <UserPlus size={14} className="text-slate-400" />
                Contact Owner
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-amber-100 text-amber-700 text-xs">
                      {contact.owner?.full_name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {contact.owner?.full_name || contact.owner?.email || 'Unassigned'}
                    </p>
                    {contact.owner?.email && contact.owner?.full_name && (
                      <p className="text-xs text-slate-500">{contact.owner.email}</p>
                    )}
                  </div>
                </div>
                {canChangeOwner && (
                  <Button variant="ghost" size="sm" onClick={() => setIsOwnerDialogOpen(true)} data-testid="change-owner-btn">
                    Change
                  </Button>
                )}
              </div>
            </Card>

            {/* Source Attribution — where this lead actually came from.
                Sits right after Contact Info because marketing agents
                jump straight to it when they're auditing campaigns; it
                hides itself politely (single line + hint) when there's
                no attribution data. */}
            <SourceAttributionCard contact={contact} />

            {/* Source Journey — chronological progression of the lead
                from "received from <source>" through assignment, first
                contact, site visit, booking. Built off the timeline
                that's already loaded so no extra fetch. */}
            <SourceJourneyCard contact={contact} timeline={timeline} />

            {/* Lead Details Card */}
            <Card className="p-5 border-slate-200" data-testid="lead-details-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-1.5">
                <Target size={14} className="text-slate-400" />
                Lead Details
              </h3>
              <div className="space-y-4">
                {/* Lead Status */}
                <div>
                  <Label className="text-xs text-slate-500">Lead Status</Label>
                  <div className="flex items-center justify-between mt-1">
                    <Badge 
                      className={`${getStatusStyle(contact.status).bg} ${getStatusStyle(contact.status).text} border ${getStatusStyle(contact.status).border} cursor-pointer`}
                      onClick={() => { setStatusSearchQuery(''); setIsStatusDialogOpen(true); }}
                    >
                      {getStatusDisplayLabel(contact.status)}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setStatusSearchQuery(''); setIsStatusDialogOpen(true); }}>
                      Change
                    </Button>
                  </div>
                </div>

                {/* Lifecycle Stage */}
                <div>
                  <Label className="text-xs text-slate-500">Lifecycle Stage</Label>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 cursor-pointer"
                      onClick={() => { setLifecycleSearchQuery(''); setIsLifecycleStageDialogOpen(true); }}
                      data-testid="lifecycle-badge"
                    >
                      {getLifecycleDisplayLabel(contact.lifecycle_stage)}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setLifecycleSearchQuery(''); setIsLifecycleStageDialogOpen(true); }}>
                      Change
                    </Button>
                  </div>
                </div>

                {/* Lead Score — bigger headline number so the
                    section actually reads as a KPI at a glance, with
                    the progress bar underneath. Color-coded band:
                    hot (≥80) red, warm (≥50) amber, cold blue. */}
                <div>
                  <Label className="text-xs text-slate-500">Lead Score</Label>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className={`text-2xl font-semibold ${
                      (contact.lead_score || 0) >= 80 ? 'text-red-600' :
                      (contact.lead_score || 0) >= 50 ? 'text-amber-600' : 'text-blue-600'
                    }`}>
                      {contact.lead_score || 0}
                    </span>
                    <span className="text-xs text-slate-400">/ 100</span>
                    <span className="text-[11px] text-slate-500 ml-auto">
                      {(contact.lead_score || 0) >= 80 ? 'Hot' :
                       (contact.lead_score || 0) >= 50 ? 'Warm' : 'Cold'}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (contact.lead_score || 0) >= 80 ? 'bg-gradient-to-r from-red-400 to-red-500' :
                        (contact.lead_score || 0) >= 50 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                        'bg-gradient-to-r from-blue-400 to-blue-500'
                      }`}
                      style={{ width: `${Math.min(100, contact.lead_score || 0)}%` }}
                    />
                  </div>
                </div>

                {/* HubSpot Sync Info - Full Card (restored from previous design) */}
                {hubspotSyncInfo && hubspotSyncInfo.is_hubspot_lead && (
                  <div className="pt-3 border-t border-slate-100">
                    <Card className="p-4 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50" data-testid="hubspot-sync-card">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-orange-700 flex items-center gap-2 text-sm">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                          </svg>
                          HubSpot Sync
                        </h3>
                        <Badge className={`${
                          hubspotSyncInfo.hubspot_sync_status === 'synced' ? 'bg-green-500' :
                          hubspotSyncInfo.hubspot_sync_status === 'failed' ? 'bg-red-500' :
                          hubspotSyncInfo.hubspot_sync_status === 'skipped' ? 'bg-yellow-500' :
                          'bg-slate-400'
                        } text-white text-xs`}>
                          {hubspotSyncInfo.hubspot_sync_status || 'Not Synced'}
                        </Badge>
                      </div>

                      <div className="space-y-2 text-sm">
                        {/* HubSpot Contact ID */}
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600 text-xs">HubSpot Contact ID</span>
                          <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-orange-200">
                            {hubspotSyncInfo.hubspot_contact_id || contact.hubspot_contact_id || '-'}
                          </span>
                        </div>

                        {/* Last Synced */}
                        {hubspotSyncInfo.hubspot_last_synced_at && (
                          <div className="flex justify-between items-center">
                            <span className="text-slate-600 text-xs">Last Synced</span>
                            <span className="text-slate-700 text-xs">{formatDateTime(hubspotSyncInfo.hubspot_last_synced_at)}</span>
                          </div>
                        )}

                        {/* Current Owner Mapping */}
                        {hubspotSyncInfo.current_owner_mapping && (
                          <div className="bg-white rounded-lg p-2 border border-orange-200 mt-2">
                            <p className="text-[10px] text-slate-500 mb-1">Current Owner HubSpot Mapping</p>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-800 text-xs">{hubspotSyncInfo.current_owner_mapping.name || 'Unknown'}</p>
                                <p className="text-[10px] text-slate-500">{hubspotSyncInfo.current_owner_mapping.email}</p>
                              </div>
                              {hubspotSyncInfo.current_owner_mapping.has_mapping ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">
                                  <Check size={10} className="mr-1" />
                                  Mapped
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">
                                  <AlertCircle size={10} className="mr-1" />
                                  No Mapping
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Sync Error */}
                        {hubspotSyncInfo.hubspot_sync_error && (
                          <div className="bg-red-50 rounded-lg p-2 border border-red-200 mt-2">
                            <p className="text-[10px] text-red-600 flex items-center gap-1">
                              <AlertCircle size={10} />
                              {hubspotSyncInfo.hubspot_sync_error}
                            </p>
                          </div>
                        )}

                        {/* Manual Sync Button */}
                        <Button
                          size="sm"
                          onClick={syncOwnerToHubspot}
                          disabled={isSyncingOwnerToHubspot || !hubspotSyncInfo.can_sync}
                          className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
                          data-testid="sync-hubspot-owner-btn"
                        >
                          {isSyncingOwnerToHubspot ? (
                            <>
                              <Loader2 size={12} className="mr-1 animate-spin" />
                              Syncing...
                            </>
                          ) : (
                            <>
                              <RefreshCw size={12} className="mr-1" />
                              Sync Owner to HubSpot
                            </>
                          )}
                        </Button>
                        
                        {!hubspotSyncInfo.can_sync && hubspotSyncInfo.current_owner_mapping && !hubspotSyncInfo.current_owner_mapping.has_mapping && (
                          <p className="text-[10px] text-slate-500 text-center mt-1">
                            Owner needs a HubSpot User ID mapping to sync
                          </p>
                        )}
                      </div>
                    </Card>
                  </div>
                )}
                
                {/* Simple HubSpot ID display for non-HubSpot leads with hubspot_contact_id */}
                {contact.hubspot_contact_id && (!hubspotSyncInfo || !hubspotSyncInfo.is_hubspot_lead) && (
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                          <Zap size={12} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-slate-700">HubSpot Linked</p>
                          <p className="text-[10px] text-slate-400">ID: {contact.hubspot_contact_id}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-xs"
                        onClick={syncOwnerToHubspot}
                        disabled={isSyncingOwnerToHubspot}
                      >
                        {isSyncingOwnerToHubspot ? <Loader2 size={12} className="animate-spin" /> : 'Sync'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tags Section — when there are no tags, render a
                    single dashed "+ Add tag" pill instead of a label
                    + grey "No tags assigned" + separate button. One
                    affordance, one click. When tags exist, the label
                    + Add button reappear so the user knows where to
                    add more. */}
                <div className="pt-3 border-t border-slate-100">
                  {(contact.tags || []).length === 0 ? (
                    <button
                      onClick={() => { setTagSearchQuery(''); setIsTagsDialogOpen(true); }}
                      className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 hover:border-slate-400 rounded-full px-3 py-1 transition-colors"
                      data-testid="add-tag-empty"
                    >
                      <Tag size={11} />
                      <Plus size={10} /> Add tag
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-slate-500 flex items-center gap-1">
                          <Tag size={12} />
                          Tags
                        </Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => { setTagSearchQuery(''); setIsTagsDialogOpen(true); }}
                        >
                          <Plus size={12} className="mr-1" /> Add
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(contact.tags || []).map((tagName, idx) => {
                          const tagInfo = availableTags.find(t => t.name === tagName);
                          const tagColor = tagInfo?.color || '#6366F1';
                          return (
                            <Badge
                              key={idx}
                              className="text-xs font-normal cursor-pointer hover:opacity-80 group"
                              style={{
                                backgroundColor: `${tagColor}20`,
                                color: tagColor,
                                borderColor: `${tagColor}40`
                              }}
                              variant="outline"
                            >
                              {tagName}
                              <X
                                size={12}
                                className="ml-1 opacity-60 hover:opacity-100 cursor-pointer"
                                onClick={(e) => { e.stopPropagation(); handleRemoveTag(tagName); }}
                              />
                            </Badge>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Custom Fields Section */}
                {customFields.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <Label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                      <Hash size={12} />
                      Custom Fields
                    </Label>
                    <div className="space-y-2">
                      {customFields.map(field => {
                        const fieldValue = contact.custom_fields?.[field.field_name] || '';
                        return (
                          <div key={field.id} className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">{field.field_name}</span>
                            {isEditing ? (
                              field.field_type === 'dropdown' ? (
                                <Select 
                                  value={editData.custom_fields?.[field.field_name] || ''} 
                                  onValueChange={(val) => setEditData({
                                    ...editData, 
                                    custom_fields: { ...editData.custom_fields, [field.field_name]: val }
                                  })}
                                >
                                  <SelectTrigger className="w-32 h-7 text-xs">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(field.options || []).map(opt => (
                                      <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field.field_type === 'checkbox' ? (
                                <input 
                                  type="checkbox" 
                                  checked={editData.custom_fields?.[field.field_name] || false}
                                  onChange={(e) => setEditData({
                                    ...editData, 
                                    custom_fields: { ...editData.custom_fields, [field.field_name]: e.target.checked }
                                  })}
                                  className="h-4 w-4"
                                />
                              ) : (
                                <Input 
                                  type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                                  value={editData.custom_fields?.[field.field_name] || ''}
                                  onChange={(e) => setEditData({
                                    ...editData, 
                                    custom_fields: { ...editData.custom_fields, [field.field_name]: e.target.value }
                                  })}
                                  className="w-32 h-7 text-xs"
                                  placeholder={`Enter ${field.field_name.toLowerCase()}`}
                                />
                              )
                            ) : (
                              <span className="text-xs text-slate-800 font-medium">
                                {field.field_type === 'checkbox' 
                                  ? (fieldValue ? '✓ Yes' : '✗ No') 
                                  : (fieldValue || '-')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Stats */}
            <Card className="p-5 border-slate-200" data-testid="quick-stats">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{calls.length}</p>
                  <p className="text-xs text-slate-500">Calls</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{emails.length}</p>
                  <p className="text-xs text-slate-500">Emails</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{whatsappMessages.length}</p>
                  <p className="text-xs text-slate-500">Messages</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{tasks.length}</p>
                  <p className="text-xs text-slate-500">Tasks</p>
                </div>
              </div>
            </Card>
          </div>

          {/* ACTIVITY COLUMN - now 8 cols, order-1 so it sits on the LEFT
              on large screens (the right rail is order-2). Wider feed
              = much more comfortable reading width for the timeline,
              messages, and emails tabs. */}
          <div className="col-span-12 lg:col-span-8 lg:order-1">
            <Card className="border-slate-200 overflow-hidden" data-testid="activity-card">
              {/* Tabs Header */}
              <div className="border-b border-slate-200 bg-white">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start bg-transparent h-12 p-0 gap-0 rounded-none overflow-x-auto">
                    {['timeline', 'activities', 'whatsapp', 'calls', 'emails', 'notes', 'tasks', 'meetings', 'audit'].map(tab => (
                      <TabsTrigger
                        key={tab}
                        value={tab}
                        className="data-[state=active]:border-b-2 data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none px-4 h-12 text-sm capitalize font-medium text-slate-500 data-[state=active]:text-slate-900"
                        data-testid={`tab-${tab}`}
                      >
                        {tab === 'whatsapp' ? 'WhatsApp' : tab === 'activities' ? 'All Activities' : tab}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {/* Tab Content */}
              <div className="p-0">
                {/* Timeline Tab */}
                {activeTab === 'timeline' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-6 space-y-4">
                      {/* Next Action Highlight - if there's an upcoming task */}
                      {tasks.filter(t => t.status !== 'completed' && t.due_date).length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-2">
                            <AlertCircle size={16} />
                            Next Action Required
                          </div>
                          {tasks.filter(t => t.status !== 'completed').slice(0, 1).map(task => (
                            <div key={task.id} className="flex items-center justify-between">
                              <div 
                                className="cursor-pointer hover:text-amber-800" 
                                onClick={() => setActiveTab('tasks')}
                              >
                                <p className="text-sm font-medium text-slate-800">{task.title}</p>
                                {task.due_date && (
                                  <p className="text-xs text-amber-600">Due: {new Date(task.due_date).toLocaleDateString()}</p>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-amber-700 border-amber-300 hover:bg-amber-100"
                                onClick={() => handleCompleteTask(task.id)}
                              >
                                <CheckSquare size={14} className="mr-1" /> Mark Done
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* UTM Attribution Summary - if available */}
                      {(contact?.utm_source || contact?.utm_campaign) && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-purple-700 font-medium text-sm mb-2">
                            <Target size={16} />
                            Marketing Attribution
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                            {contact.utm_source && (
                              <div>
                                <span className="text-slate-500">Source:</span>
                                <span className="ml-1 font-medium text-slate-700">{contact.utm_source}</span>
                              </div>
                            )}
                            {contact.utm_medium && (
                              <div>
                                <span className="text-slate-500">Medium:</span>
                                <span className="ml-1 font-medium text-slate-700">{contact.utm_medium}</span>
                              </div>
                            )}
                            {contact.utm_campaign && (
                              <div>
                                <span className="text-slate-500">Campaign:</span>
                                <span className="ml-1 font-medium text-slate-700">{contact.utm_campaign}</span>
                              </div>
                            )}
                            {contact.campaign_id && (
                              <div>
                                <span className="text-slate-500">Campaign ID:</span>
                                <span className="ml-1 font-medium text-slate-700">{contact.campaign_id}</span>
                              </div>
                            )}
                            {contact.adset_name && (
                              <div>
                                <span className="text-slate-500">Ad Set:</span>
                                <span className="ml-1 font-medium text-slate-700">{contact.adset_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Quick Add — slightly more inviting than two
                          plain outline buttons. Subtle colored backgrounds
                          and matching icons signal what kind of entry
                          each one creates so the eye doesn't need to
                          read both labels. */}
                      <div className="flex gap-2 pb-4 mb-4 border-b border-slate-100">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsNoteDialogOpen(true)}
                          className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-300"
                        >
                          <FileText size={14} /> Add note
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsTaskDialogOpen(true)}
                          className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-300"
                        >
                          <CheckSquare size={14} /> New task
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setIsMeetingDialogOpen(true)}
                          className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-300"
                        >
                          <Calendar size={14} /> Log meeting
                        </Button>
                      </div>

                      {timeline.length === 0 ? (
                        <div className="text-center py-16">
                          <Activity size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No activities yet</p>
                        </div>
                      ) : (
                        <div className="relative mt-2">
                          {/* Timeline line */}
                          <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />
                          
                          {timeline.map((event, idx) => {
                            // Pass the full event so the helpers can
                            // also sniff the title for failure markers.
                            const Icon = getEventIcon(event);
                            const color = getEventColor(event);
                            const itemId = event.id || `timeline-${idx}`;
                            const isExpanded = expandedTimelineItems[itemId];
                            // Split off any raw JSON blob from the description
                            // so the bubble shows a friendly summary and the
                            // technical detail sits behind a toggle.
                            const { friendly, details } = formatTimelineDescription(event.description);
                            const hasLongContent = friendly && friendly.length > 150;

                            return (
                              <div key={event.id || idx} className="relative flex gap-4 pb-6" data-testid={`timeline-event-${idx}`}>
                                <div className={`relative z-10 w-10 h-10 rounded-full ${color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                  <Icon size={16} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="font-medium text-slate-900 text-sm leading-tight">{event.title}</p>
                                      {event.user && (
                                        <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                                          <User size={10} className="text-slate-400" />
                                          <span>by</span>
                                          <span className="font-medium text-slate-600">{event.user}</span>
                                        </p>
                                      )}
                                    </div>
                                    <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                      {formatRelativeTime(event.date)}
                                    </span>
                                  </div>
                                  {friendly && (() => {
                                    // Split the description into chip-eligible
                                    // "Key: Value" lines and free-form text.
                                    // Chips render as label + badge; text
                                    // lines render as paragraphs.
                                    const parts = parseDescriptionLines(friendly);
                                    const chips = parts.filter(p => p.type === 'chip');
                                    const texts = parts.filter(p => p.type === 'text');
                                    const longText = texts.map(t => t.text).join(' ');
                                    const isLong = longText.length > 150;
                                    return (
                                      <div className="mt-3 space-y-2">
                                        {chips.length > 0 && (
                                          <div className="flex flex-wrap gap-1.5">
                                            {chips.map(c => (
                                              <span key={c.key} className="inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                                                <span className="text-slate-500">{c.label}:</span>
                                                <span className="font-medium text-slate-700">{c.value}</span>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {texts.length > 0 && (
                                          <p className={`text-sm text-slate-600 leading-relaxed ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
                                            {texts.map(t => t.text).join('\n')}
                                          </p>
                                        )}
                                        {(isLong || details) && (
                                          <button
                                            onClick={() => toggleTimelineExpand(itemId)}
                                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                          >
                                            {isExpanded ? <>Show less</> : (details ? <>View details</> : <>Read more</>)}
                                          </button>
                                        )}
                                        {isExpanded && details && (
                                          <pre className="text-[11px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 max-h-56 overflow-y-auto whitespace-pre-wrap break-words leading-relaxed">
                                            {prettyPrintMaybeJson(details)}
                                          </pre>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  
                                  {/* UTM Attribution for Lead Created event */}
                                  {event.type === 'lead_created' && event.utm && (
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                      <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                                        <Target size={12} /> Attribution
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {event.utm.utm_source && (
                                          <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700">
                                            {event.utm.utm_source}
                                          </Badge>
                                        )}
                                        {event.utm.utm_medium && (
                                          <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                                            {event.utm.utm_medium}
                                          </Badge>
                                        )}
                                        {event.utm.utm_campaign && (
                                          <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                                            {event.utm.utm_campaign}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Call outcome badge */}
                                  {event.source === 'call' && event.outcome && (
                                    <Badge 
                                      variant="outline" 
                                      className={`mt-2 text-xs ${
                                        event.outcome === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                        event.outcome === 'no_answer' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                        'bg-slate-50 text-slate-600 border-slate-200'
                                      }`}
                                    >
                                      {event.outcome.replace(/_/g, ' ')}
                                    </Badge>
                                  )}
                                  
                                  {/* Task status badge */}
                                  {event.source === 'task' && event.status && (
                                    <Badge 
                                      variant="outline" 
                                      className={`mt-2 text-xs ${
                                        event.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                        event.status === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' :
                                        'bg-amber-50 text-amber-700 border-amber-200'
                                      }`}
                                    >
                                      {event.status}
                                    </Badge>
                                  )}
                                  
                                  {/* Email status indicators */}
                                  {event.source === 'email' && (event.opened || event.replied) && (
                                    <div className="flex gap-2 mt-2">
                                      {event.opened && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                          <MailOpen size={10} className="mr-1" /> Opened
                                        </Badge>
                                      )}
                                      {event.replied && (
                                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                          <Check size={10} className="mr-1" /> Replied
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Call recording */}
                                  {event.source === 'call' && event.metadata?.recording_url && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="mt-2 gap-1.5"
                                      onClick={() => toggleRecordingPlayback(event.metadata)}
                                    >
                                      {playingRecording === event.metadata.id ? (
                                        <><PauseCircle size={14} /> Pause</>
                                      ) : (
                                        <><PlayCircle size={14} /> Play Recording</>
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* All Activities Tab */}
                {activeTab === 'activities' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-slate-800">All Activities</h3>
                        <Button variant="ghost" size="sm" onClick={fetchActivities}>
                          <RefreshCw size={14} className="mr-1" /> Refresh
                        </Button>
                      </div>
                      {activities.filter(shouldShowActivity).length === 0 ? (
                        <div className="text-center py-16">
                          <Activity size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No activities recorded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {activities.filter(shouldShowActivity).map((activity, idx) => {
                            // Use the same shared helpers as the Timeline
                            // tab so the All Activities view stays in lockstep
                            // with the Timeline rendering: correct icon for
                            // failed AI calls (red AlertCircle), pretty-printed
                            // JSON in the "View details" panel, "Key: Value"
                            // chips, byline with user icon, etc.
                            const actType = activity.type || activity.activity_type || 'activity';
                            const eventLike = {
                              type: actType,
                              title: activity.title || actType?.replace(/_/g, ' ') || 'Activity',
                            };
                            const ActivityIcon = getEventIcon(eventLike);
                            const colorBg = getEventColor(eventLike);
                            const activityItemId = `activity-${activity.id || idx}`;
                            const isActivityExpanded = expandedTimelineItems[activityItemId];
                            const rawDesc = activity.description || activity.content || '';
                            const { friendly: actFriendly, details: actDetails } = formatTimelineDescription(rawDesc);
                            const userName = activity.performed_by_name || activity.user_name || (activity.is_automated ? 'System' : 'System');

                            // Split structured chip lines out of the
                            // description so they render as label+value pills.
                            const parts = parseDescriptionLines(actFriendly);
                            const chips = parts.filter(p => p.type === 'chip');
                            const texts = parts.filter(p => p.type === 'text');
                            const longText = texts.map(t => t.text).join(' ');
                            const isLong = longText.length > 150;

                            return (
                              <Card key={activity.id || idx} className="p-4 border-slate-200">
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-full ${colorBg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                    <ActivityIcon size={16} className="text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <p className="font-medium text-sm text-slate-900 leading-tight">
                                          {eventLike.title}
                                        </p>
                                        <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                                          <User size={10} className="text-slate-400" />
                                          <span>by</span>
                                          <span className="font-medium text-slate-600">{userName}</span>
                                        </p>
                                      </div>
                                      <span className="text-[11px] text-slate-400 whitespace-nowrap">
                                        {formatDateTime(activity.timestamp || activity.created_at)}
                                      </span>
                                    </div>

                                    {actFriendly && (
                                      <div className="mt-3 space-y-2">
                                        {chips.length > 0 && (
                                          <div className="flex flex-wrap gap-1.5">
                                            {chips.map(c => (
                                              <span key={c.key} className="inline-flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                                                <span className="text-slate-500">{c.label}:</span>
                                                <span className="font-medium text-slate-700">{c.value}</span>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {texts.length > 0 && (
                                          <p className={`text-sm text-slate-600 leading-relaxed ${!isActivityExpanded && isLong ? 'line-clamp-3' : ''}`}>
                                            {texts.map(t => t.text).join('\n')}
                                          </p>
                                        )}
                                        {(isLong || actDetails) && (
                                          <button
                                            onClick={() => toggleTimelineExpand(activityItemId)}
                                            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                          >
                                            {isActivityExpanded ? 'Show less' : (actDetails ? 'View details' : 'Read more')}
                                          </button>
                                        )}
                                        {isActivityExpanded && actDetails && (
                                          <pre className="text-[11px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 max-h-56 overflow-y-auto whitespace-pre-wrap break-words leading-relaxed">
                                            {prettyPrintMaybeJson(actDetails)}
                                          </pre>
                                        )}
                                      </div>
                                    )}

                                    {/* Call metadata still gets its
                                        specialised rendering since the
                                        duration / status badges aren't
                                        in the description text. */}
                                    {actType === 'call' && activity.metadata && (
                                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                        {activity.metadata.duration_seconds > 0 && (
                                          <span>
                                            Duration: {Math.floor(activity.metadata.duration_seconds / 60)}:{(activity.metadata.duration_seconds % 60).toString().padStart(2, '0')}
                                          </span>
                                        )}
                                        {activity.metadata.status && (
                                          <Badge variant="outline" className="text-xs">{activity.metadata.status}</Badge>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* WhatsApp Tab */}
                {activeTab === 'whatsapp' && (
                  <div className="flex flex-col h-[calc(100vh-280px)]">
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-3">
                        {whatsappMessages.length === 0 ? (
                          <div className="text-center py-16">
                            <MessageCircle size={40} className="mx-auto text-slate-300 mb-3" />
                            <p className="text-slate-500">No messages yet</p>
                          </div>
                        ) : (
                          whatsappMessages.map((msg, idx) => (
                            <div
                              key={msg.id || idx}
                              className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                                  msg.direction === 'outgoing'
                                    ? 'bg-green-500 text-white rounded-br-md'
                                    : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                                }`}
                              >
                                <p className="text-sm">{msg.message || msg.text || msg.body}</p>
                                <p className={`text-[10px] mt-1 ${msg.direction === 'outgoing' ? 'text-green-100' : 'text-slate-400'}`}>
                                  {formatDateTime(msg.timestamp || msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                    {/* Message Input */}
                    <div className="p-4 border-t border-slate-200 bg-slate-50">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newWhatsappMessage}
                          onChange={(e) => setNewWhatsappMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendWhatsApp()}
                          className="bg-white"
                          data-testid="whatsapp-input"
                        />
                        <Button
                          onClick={handleSendWhatsApp}
                          disabled={sendingWhatsapp || !newWhatsappMessage.trim()}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid="whatsapp-send-btn"
                        >
                          {sendingWhatsapp ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Calls Tab */}
                {activeTab === 'calls' && (
                  <div className="flex flex-col h-[calc(100vh-280px)]">
                    {/* Header with actions */}
                    <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                            <PhoneCall size={18} className="text-blue-500" />
                            Call History
                            <span className="text-xs text-slate-400 font-normal">(auto-refreshes)</span>
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">All calls made to this contact</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={fetchCalls} className="text-blue-600 hover:bg-blue-100">
                            <RefreshCw size={14} className="mr-1" /> Refresh
                          </Button>
                          <Button onClick={handleCallWithPhoneDialog} disabled={isInitiatingCall || !contact?.phone} className="bg-blue-600 hover:bg-blue-700">
                            <PhoneCall size={14} className="mr-1" />
                            {isInitiatingCall ? 'Calling...' : 'Make Call'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <ScrollArea className="flex-1">
                      <div className="p-4 space-y-3">
                        {calls.length === 0 ? (
                          <div className="text-center py-16">
                            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                              <PhoneCall size={32} className="text-blue-400" />
                            </div>
                            <p className="text-slate-600 font-medium">No calls yet</p>
                            <p className="text-sm text-slate-400 mt-2 max-w-xs mx-auto">
                              Click "Make Call" to start a recorded call with this contact
                            </p>
                          </div>
                        ) : (
                          calls.map((call, idx) => (
                            <Card key={call.id || idx} className="p-4 border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    call.status === 'completed' ? 'bg-green-100' : 
                                    call.status === 'failed' ? 'bg-red-100' : 'bg-blue-100'
                                  }`}>
                                    <PhoneCall size={18} className={
                                      call.status === 'completed' ? 'text-green-600' : 
                                      call.status === 'failed' ? 'text-red-600' : 'text-blue-600'
                                    } />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">
                                      {call.type === 'user_outbound' ? 'Outbound Call' : 
                                       call.type === 'ai_outbound' ? 'AI Agent Call' : 
                                       call.type === 'bridge' || call.call_type === 'bridge' ? 'WhatsApp Call Bridge' :
                                       call.direction === 'inbound' ? 'Incoming Call' : 'Call'}
                                    </p>
                                    <p className="text-sm text-slate-500">
                                      {call.caller_name || call.user_name || 'Unknown'} → {contact?.name}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  {getCallStatusBadge(call.status)}
                                  <p className="text-xs text-slate-400 mt-1">
                                    {formatDateTime(call.created_at || call.start_time)}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Call Duration */}
                              {call.duration_seconds > 0 && (
                                <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                                  <Clock size={14} />
                                  Duration: {formatCallDuration(call.duration_seconds)}
                                </div>
                              )}
                              
                              {/* Recording Player */}
                              {(call.has_recording || call.recording_url) && (
                                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Sparkles size={16} className="text-blue-500" />
                                      <span className="text-sm font-medium">Recording Available</span>
                                      {call.duration_seconds && (
                                        <span className="text-xs text-slate-400">
                                          ({formatCallDuration(call.recording_duration || call.duration_seconds)})
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <audio 
                                        id={`audio-${call.id}`}
                                        src={getPlayableRecordingUrl(call.id)}
                                        onEnded={() => setPlayingRecording(null)}
                                      />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          const audio = document.getElementById(`audio-${call.id}`);
                                          if (playingRecording === call.id) {
                                            audio?.pause();
                                            setPlayingRecording(null);
                                          } else {
                                            if (playingRecording) {
                                              const otherAudio = document.getElementById(`audio-${playingRecording}`);
                                              if (otherAudio) otherAudio.pause();
                                            }
                                            audio?.play();
                                            setPlayingRecording(call.id);
                                          }
                                        }}
                                      >
                                        {playingRecording === call.id ? (
                                          <><PauseCircle size={14} className="mr-1" /> Pause</>
                                        ) : (
                                          <><PlayCircle size={14} className="mr-1" /> Play</>
                                        )}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => window.open(getPlayableRecordingUrl(call.id), '_blank')}
                                      >
                                        <ExternalLink size={14} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Call Transcript */}
                              {call.transcript && (
                                <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                                  <div className="flex items-center gap-2 mb-3">
                                    <FileText size={16} className="text-purple-600" />
                                    <span className="text-sm font-semibold text-purple-700">Call Transcript</span>
                                    {call.transcribed_at && (
                                      <span className="text-xs text-purple-400 ml-auto">
                                        Transcribed {formatDateTime(call.transcribed_at)}
                                      </span>
                                    )}
                                  </div>
                                  <div className="bg-white rounded-lg p-3 max-h-48 overflow-y-auto text-sm text-slate-700 leading-relaxed border space-y-2">
                                    {/* Handle both string and array transcript formats */}
                                    {typeof call.transcript === 'string' ? (
                                      <p className="whitespace-pre-wrap">{call.transcript}</p>
                                    ) : Array.isArray(call.transcript) ? (
                                      call.transcript.map((msg, idx) => (
                                        <div key={idx} className={`p-2 rounded ${msg.role === 'agent' ? 'bg-blue-50 text-blue-800' : 'bg-gray-50 text-gray-800'}`}>
                                          <span className="font-semibold text-xs uppercase">{msg.role === 'agent' ? 'AI Agent' : 'Lead'}:</span>
                                          <span className="ml-2">{msg.message}</span>
                                        </div>
                                      ))
                                    ) : (
                                      <span className="text-gray-400">No transcript available</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Call Analysis Section (Intent, Lead Quality, Summary) */}
                              {(call.intent || call.summary || call.lead_quality) && (
                                <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-100">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Bot size={16} className="text-amber-600" />
                                    <span className="font-medium text-amber-800">Call Analysis</span>
                                  </div>
                                  
                                  {/* Intent Badges Row */}
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {call.intent && (
                                      <Badge className={`${
                                        call.intent === 'interested_buyer' || call.intent === 'interested_investor' ? 'bg-green-100 text-green-700' :
                                        call.intent === 'not_interested' ? 'bg-red-100 text-red-700' :
                                        call.intent === 'callback_requested' || call.intent === 'meeting_scheduled' ? 'bg-blue-100 text-blue-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {call.intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </Badge>
                                    )}
                                    {call.lead_quality && (
                                      <Badge className={`${
                                        call.lead_quality === 'hot' ? 'bg-red-100 text-red-700' :
                                        call.lead_quality === 'warm' ? 'bg-orange-100 text-orange-700' :
                                        'bg-blue-100 text-blue-700'
                                      }`}>
                                        {call.lead_quality.toUpperCase()} Lead
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Summary */}
                                  {call.summary && (
                                    <p className="text-sm text-slate-700">{call.summary}</p>
                                  )}
                                </div>
                              )}
                              
                              {/* AI Summary (structured format) */}
                              {call.ai_summary && (
                                <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-100">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Bot size={16} className="text-amber-600" />
                                    <span className="text-sm font-semibold text-amber-700">AI Call Summary</span>
                                    {call.qualification_score && (
                                      <Badge className={`ml-auto ${
                                        call.qualification_score === 'Hot' ? 'bg-red-100 text-red-700' :
                                        call.qualification_score === 'Warm' ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-600'
                                      }`}>
                                        {call.qualification_score}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* Display structured summary if available */}
                                  {call.structured_summary ? (
                                    <div className="space-y-2 text-sm">
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                        {call.structured_summary.nature_of_lead && call.structured_summary.nature_of_lead !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Nature of Lead:</span> <span className="font-medium text-slate-700">{call.structured_summary.nature_of_lead}</span></div>
                                        )}
                                        {call.structured_summary.project_of_interest && call.structured_summary.project_of_interest !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Project:</span> <span className="font-medium text-slate-700">{call.structured_summary.project_of_interest}</span></div>
                                        )}
                                        {call.structured_summary.type_of_unit && call.structured_summary.type_of_unit !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Unit Type:</span> <span className="font-medium text-slate-700">{call.structured_summary.type_of_unit}</span></div>
                                        )}
                                        {call.structured_summary.budget_range && call.structured_summary.budget_range !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Budget:</span> <span className="font-medium text-emerald-700">{call.structured_summary.budget_range}</span></div>
                                        )}
                                        {call.structured_summary.location && call.structured_summary.location !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Location:</span> <span className="font-medium text-slate-700">{call.structured_summary.location}</span></div>
                                        )}
                                        {call.structured_summary.payment_plan_preference && call.structured_summary.payment_plan_preference !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Payment Plan:</span> <span className="font-medium text-slate-700">{call.structured_summary.payment_plan_preference}</span></div>
                                        )}
                                        {call.structured_summary.timeline_to_purchase && call.structured_summary.timeline_to_purchase !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Timeline:</span> <span className="font-medium text-slate-700">{call.structured_summary.timeline_to_purchase}</span></div>
                                        )}
                                        {call.structured_summary.preferred_method_of_contact && call.structured_summary.preferred_method_of_contact !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Preferred Contact:</span> <span className="font-medium text-slate-700">{call.structured_summary.preferred_method_of_contact}</span></div>
                                        )}
                                        {call.structured_summary.preferred_time_to_contact && call.structured_summary.preferred_time_to_contact !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Best Time:</span> <span className="font-medium text-slate-700">{call.structured_summary.preferred_time_to_contact}</span></div>
                                        )}
                                        {call.structured_summary.purpose && call.structured_summary.purpose !== 'Not Provided' && (
                                          <div><span className="text-slate-500">Purpose:</span> <span className="font-medium text-slate-700">{call.structured_summary.purpose}</span></div>
                                        )}
                                      </div>
                                      
                                      {/* Narrative summary */}
                                      {call.summary && (
                                        <div className="mt-3 pt-3 border-t border-amber-200">
                                          <p className="text-slate-700">{call.summary}</p>
                                        </div>
                                      )}
                                      
                                      {/* Next steps */}
                                      {call.next_steps && (
                                        <div className="mt-2 flex items-start gap-2">
                                          <span className="text-slate-500">Next Steps:</span>
                                          <span className="font-medium text-blue-700">{call.next_steps}</span>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    /* Fallback to raw text display */
                                    <p className="text-sm text-slate-700 whitespace-pre-line">{call.ai_summary}</p>
                                  )}
                                </div>
                              )}

                              {/* Call Notes */}
                              {call.notes && (
                                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                  <p className="text-sm text-slate-600">{call.notes}</p>
                                </div>
                              )}
                            </Card>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}

                {/* Emails Tab */}
                {activeTab === 'emails' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4 space-y-3">
                      <Button variant="outline" onClick={() => setIsEmailDialogOpen(true)} className="w-full gap-2 mb-4">
                        <Plus size={14} /> Compose Email
                      </Button>
                      {emails.length === 0 ? (
                        <div className="text-center py-16">
                          <Mail size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No emails yet</p>
                        </div>
                      ) : (
                        emails.map((email, idx) => (
                          <Card key={email.id || idx} className="p-4 border-slate-200 hover:border-slate-300 cursor-pointer">
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                email.direction === 'received' ? 'bg-purple-100 text-purple-600' : 'bg-indigo-100 text-indigo-600'
                              }`}>
                                {email.direction === 'received' ? <MailOpen size={14} /> : <Mail size={14} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-slate-900 truncate">{email.subject}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {email.direction === 'received' ? `From: ${email.from}` : `To: ${email.to}`}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">{formatDateTime(email.sent_at || email.received_at)}</p>
                              </div>
                            </div>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4 space-y-3">
                      <Button variant="outline" onClick={() => setIsNoteDialogOpen(true)} className="w-full gap-2 mb-4">
                        <Plus size={14} /> Add Note
                      </Button>
                      {notes.length === 0 ? (
                        <div className="text-center py-16">
                          <FileText size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No notes yet</p>
                        </div>
                      ) : (
                        notes.map((note, idx) => (
                          <Card key={note.id || idx} className="p-4 border-slate-200">
                            <p className="text-sm text-slate-700">{note.content || note.description || note.note}</p>
                            <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                              <span>{note.performed_by_name || note.created_by_name || 'Unknown'}</span>
                              <span>•</span>
                              <span>{formatRelativeTime(note.timestamp || note.created_at)}</span>
                            </div>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4 space-y-3">
                      <Button variant="outline" onClick={() => setIsTaskDialogOpen(true)} className="w-full gap-2 mb-4">
                        <Plus size={14} /> Add Task
                      </Button>
                      {tasks.length === 0 ? (
                        <div className="text-center py-16">
                          <CheckSquare size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No tasks yet</p>
                        </div>
                      ) : (
                        tasks.map((task, idx) => (
                          <Card key={task.id || idx} className={`p-4 border-slate-200 ${task.status === 'completed' ? 'opacity-60' : ''}`}>
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => task.status !== 'completed' && handleCompleteTask(task.id)}
                                disabled={task.status === 'completed'}
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                                  task.status === 'completed' ? 'bg-emerald-500 border-emerald-500 cursor-default' : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50'
                                }`}
                              >
                                {task.status === 'completed' && <CheckCircle size={12} className="text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className={`font-medium text-sm ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                  {task.title}
                                </p>
                                {task.description && (
                                  <p className="text-xs text-slate-500 mt-1">{task.description}</p>
                                )}
                                <div className="flex items-center gap-3 mt-2">
                                  {task.due_date && (
                                    <span className="text-xs text-slate-400 flex items-center gap-1">
                                      <Clock size={10} /> {formatDate(task.due_date)}
                                    </span>
                                  )}
                                  <Badge variant="outline" className="text-[10px] capitalize">
                                    {task.priority}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* Meetings Tab */}
                {activeTab === 'meetings' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4 space-y-3">
                      <Button variant="outline" onClick={() => setIsMeetingDialogOpen(true)} className="w-full gap-2 mb-4">
                        <Plus size={14} /> Schedule Meeting
                      </Button>
                      {meetings.length === 0 ? (
                        <div className="text-center py-16">
                          <Calendar size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No meetings scheduled</p>
                        </div>
                      ) : (
                        meetings.map((meeting, idx) => {
                          const meetingDate = new Date(meeting.start_time);
                          const isPast = meetingDate < new Date();
                          return (
                            <Card key={meeting.id || idx} className={`p-4 border-slate-200 ${isPast ? 'opacity-60' : ''}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    meeting.meeting_type === 'video' ? 'bg-blue-100 text-blue-600' :
                                    meeting.meeting_type === 'in_person' ? 'bg-green-100 text-green-600' :
                                    'bg-purple-100 text-purple-600'
                                  }`}>
                                    {meeting.meeting_type === 'video' ? <Video size={18} /> :
                                     meeting.meeting_type === 'in_person' ? <MapPin size={18} /> :
                                     <PhoneCall size={18} />}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm text-slate-900">{meeting.title}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {formatDateTime(meeting.start_time)} • {meeting.duration_minutes} mins
                                    </p>
                                    {meeting.location && (
                                      <p className="text-xs text-slate-400 mt-1">{meeting.location}</p>
                                    )}
                                  </div>
                                </div>
                                <Badge variant="outline" className={isPast ? 'bg-slate-100' : 'bg-green-100 text-green-700'}>
                                  {isPast ? 'Past' : 'Upcoming'}
                                </Badge>
                              </div>
                              {meeting.description && (
                                <p className="text-sm text-slate-600 mt-3 pl-13">{meeting.description}</p>
                              )}
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                )}

                {/* Audit Tab */}
                {activeTab === 'audit' && (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <History size={16} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">Change History</span>
                      </div>
                      {auditLog.length === 0 ? (
                        <div className="text-center py-16">
                          <History size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-500">No changes recorded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {auditLog.map((entry, idx) => (
                            <div key={entry.id || idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                <User size={14} className="text-slate-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-slate-700">
                                    {entry.user_name || entry.changed_by || 'System'}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatDateTime(entry.timestamp || entry.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">
                                  {entry.action === 'status_change' && (
                                    <>Changed status from <Badge variant="outline" className="mx-1">{entry.old_value}</Badge> to <Badge variant="outline" className="mx-1">{entry.new_value}</Badge></>
                                  )}
                                  {entry.action === 'owner_change' && (
                                    <>Changed owner from <strong>{entry.old_value}</strong> to <strong>{entry.new_value}</strong></>
                                  )}
                                  {entry.action === 'field_update' && (
                                    <>Updated <strong>{entry.field_name}</strong></>
                                  )}
                                  {!['status_change', 'owner_change', 'field_update'].includes(entry.action) && (
                                    <>{entry.description || entry.action}</>
                                  )}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </Card>
          </div>

          {/* EXTRA WIDGETS — Pending Tasks / Upcoming Meetings strip.
              Lives below the activity feed on large screens (order-3)
              spanning the same width so the right rail above keeps
              flowing visually. Stacks below the rail on smaller
              screens (col-span-12 default). */}
          <div className="col-span-12 lg:col-span-8 lg:order-3 space-y-4">
            {/* Pending Tasks */}
            {pendingTasks.length > 0 && (
              <Card className="p-5 border-slate-200" data-testid="pending-tasks-card">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckSquare size={14} className="text-cyan-500" />
                  Pending Tasks
                  <Badge variant="secondary" className="ml-auto">{pendingTasks.length}</Badge>
                </h3>
                <div className="space-y-2">
                  {pendingTasks.slice(0, 3).map((task, idx) => (
                    <div 
                      key={task.id || idx} 
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => setActiveTab('tasks')}
                    >
                      <div 
                        className="w-4 h-4 rounded border border-slate-300 flex-shrink-0 hover:border-cyan-500 hover:bg-cyan-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompleteTask(task.id);
                        }}
                      />
                      <span className="text-sm text-slate-700 truncate">{task.title}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Upcoming Meetings */}
            {upcomingMeetings.length > 0 && (
              <Card className="p-5 border-slate-200" data-testid="upcoming-meetings-card">
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Calendar size={14} className="text-orange-500" />
                  Upcoming Meetings
                </h3>
                <div className="space-y-2">
                  {upcomingMeetings.slice(0, 2).map((meeting, idx) => (
                    <div key={meeting.id || idx} className="p-3 bg-orange-50 border border-orange-100 rounded-lg">
                      <p className="text-sm font-medium text-slate-800">{meeting.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatDateTime(meeting.start_time)}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* DIALOGS */}

      {/* Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-slate-900 text-lg font-semibold">Add Note</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Write your note..."
              rows={5}
              className="resize-none w-full"
              data-testid="note-textarea"
            />
          </div>
          <DialogFooter className="pt-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!newNote.trim()} className="bg-purple-600 hover:bg-purple-700 text-white">
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Approval Dialog — opens from the "More" menu.
          Pre-fills title and related-record so the approver gets
          useful context. */}
      <RequestApprovalDialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
        defaultType="discount_approval"
        defaultTitle={`Approval for ${contact?.name || contact?.email || 'this contact'}`}
        relatedId={contact?.id}
        relatedType="contact"
      />

      {/* Task Dialog */}
      <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-white p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg font-semibold">Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Title <span className="text-red-500">*</span></Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Task title..."
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Description <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Due Date <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Reminder Section - Optional */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2">
                  <Clock size={14} className="text-slate-500" />
                  Set Reminder <span className="text-slate-400 text-xs font-normal">(optional)</span>
                </Label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTask.reminder_enabled}
                    onChange={(e) => {
                      setNewTask({ ...newTask, reminder_enabled: e.target.checked });
                      // Play reminder sound when enabling
                      if (e.target.checked) {
                        import('@/utils/notificationSound').then(({ default: notificationSound }) => {
                          notificationSound.playNotificationSound('reminder');
                        });
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {newTask.reminder_enabled && (
                <Select value={newTask.reminder_time} onValueChange={(v) => setNewTask({ ...newTask, reminder_time: v })}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Remind me..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes before</SelectItem>
                    <SelectItem value="30">30 minutes before</SelectItem>
                    <SelectItem value="60">1 hour before</SelectItem>
                    <SelectItem value="120">2 hours before</SelectItem>
                    <SelectItem value="1440">1 day before</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Notification Section - Optional */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Bell size={14} className="text-slate-500" />
                  Send Notification <span className="text-slate-400 text-xs font-normal">(optional)</span>
                </Label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newTask.notification_enabled}
                    onChange={(e) => {
                      setNewTask({ ...newTask, notification_enabled: e.target.checked });
                      // Play sound when enabling notification
                      if (e.target.checked) {
                        import('@/utils/notificationSound').then(({ default: notificationSound }) => {
                          notificationSound.playNotificationSound('task');
                        });
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              {newTask.notification_enabled && (
                <p className="text-xs text-slate-500 mt-2">
                  You'll receive a browser notification and sound alert when this task is due.
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="pt-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTask} disabled={!newTask.title.trim()} className="bg-purple-600 hover:bg-purple-700 text-white">
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog - Searchable */}
      <Dialog open={isStatusDialogOpen} onOpenChange={(open) => { setIsStatusDialogOpen(open); if (!open) setStatusSearchQuery(''); }}>
        <DialogContent className="sm:max-w-md bg-white p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2">
              Change Lead Status
              <Badge variant="outline" className={selectedDepartment === 'telesales' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-purple-100 text-purple-700 border-purple-300'}>
                {selectedDepartment === 'telesales' ? 'Telesales' : 'Sales'}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Select a status for this lead
            </DialogDescription>
          </DialogHeader>
          
          {/* Search Input */}
          <div className="relative">
            <Input
              placeholder="Search statuses..."
              value={statusSearchQuery}
              onChange={(e) => setStatusSearchQuery(e.target.value)}
              className="w-full"
              autoFocus
              data-testid="status-search-input"
            />
            {statusSearchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setStatusSearchQuery('')}
              >
                <X size={14} />
              </Button>
            )}
          </div>
          
          {/* Scrollable Status List */}
          <ScrollArea className="max-h-[300px] mt-2">
            <div className="space-y-1 pr-2">
              {filteredLeadStatuses.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No statuses found</p>
              ) : (
                filteredLeadStatuses.map(status => {
                  const statusValue = status.value || status.name;
                  const statusLabel = status.display_name || status.name || status.label || statusValue;
                  const lifecycleStage = status.lifecycle_stage;
                  const isSelected = contact.status === statusValue;
                  
                  return (
                    <Button
                      key={statusValue}
                      variant={isSelected ? 'default' : 'outline'}
                      className={`w-full justify-start text-left h-auto py-2.5 px-3 ${isSelected ? 'bg-slate-900' : ''}`}
                      onClick={() => { handleStatusChange(statusValue); setStatusSearchQuery(''); }}
                      data-testid={`status-${statusValue}`}
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex items-center gap-2">
                          {isSelected && <CheckCircle size={14} className="flex-shrink-0 text-green-500" />}
                          <span className="truncate">{statusLabel}</span>
                        </div>
                        {lifecycleStage && (
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">
                            {lifecycleStage}
                          </span>
                        )}
                      </div>
                    </Button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Lifecycle Stage Dialog - Two-step selection with reasons */}
      <Dialog open={isLifecycleStageDialogOpen} onOpenChange={(open) => { 
        setIsLifecycleStageDialogOpen(open); 
        if (!open) { 
          setLifecycleSearchQuery(''); 
          setSelectedLifecycleForReason(null); 
        } 
      }}>
        <DialogContent className="sm:max-w-md bg-white p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2">
              {selectedLifecycleForReason ? 'Select Reason' : 'Change Lifecycle Stage'}
              <Badge variant="outline" className={selectedDepartment === 'telesales' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-purple-100 text-purple-700 border-purple-300'}>
                {selectedDepartment === 'telesales' ? 'Telesales' : 'Sales'}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedLifecycleForReason 
                ? `Select a reason for "${selectedLifecycleForReason.display_name || selectedLifecycleForReason.name}"`
                : 'Select a lifecycle stage for this lead'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Back button when showing reasons */}
          {selectedLifecycleForReason && (
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 text-slate-600"
              onClick={() => setSelectedLifecycleForReason(null)}
            >
              <ArrowLeft size={14} className="mr-1" /> Back to stages
            </Button>
          )}
          
          {/* Search Input */}
          {!selectedLifecycleForReason && (
            <div className="relative">
              <Input
                placeholder="Search lifecycle stages..."
                value={lifecycleSearchQuery}
                onChange={(e) => setLifecycleSearchQuery(e.target.value)}
                className="w-full"
                autoFocus
                data-testid="lifecycle-search-input"
              />
              {lifecycleSearchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setLifecycleSearchQuery('')}
                >
                  <X size={14} />
                </Button>
              )}
            </div>
          )}
          
          {/* Scrollable List - Either Lifecycle Stages or Reasons */}
          <ScrollArea className="max-h-[300px] mt-2">
            <div className="space-y-1 pr-2">
              {selectedLifecycleForReason ? (
                // Show reasons (lead statuses) for selected lifecycle stage
                <>
                  {getReasonsForLifecycle(selectedLifecycleForReason.name).length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No reasons available</p>
                  ) : (
                    getReasonsForLifecycle(selectedLifecycleForReason.name).map(reason => {
                      const reasonValue = reason.name || reason.value;
                      const reasonLabel = reason.display_name || reason.name || reasonValue;
                      const isSelected = contact.status === reasonValue;
                      
                      return (
                        <Button
                          key={reasonValue}
                          variant={isSelected ? 'default' : 'outline'}
                          className={`w-full justify-start text-left h-auto py-2.5 px-3 ${isSelected ? 'bg-slate-900' : ''}`}
                          onClick={() => handleLifecycleWithReason(selectedLifecycleForReason.name, reasonValue)}
                          data-testid={`reason-${reasonValue}`}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: reason.color || '#6b7280' }}
                            />
                            {isSelected && <CheckCircle size={14} className="flex-shrink-0 text-green-500" />}
                            <span className="truncate">{reasonLabel}</span>
                          </div>
                        </Button>
                      );
                    })
                  )}
                </>
              ) : (
                // Show lifecycle stages
                <>
                  {filteredLifecycleStages.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No lifecycle stages found</p>
                  ) : (
                    filteredLifecycleStages.map(stage => {
                      const stageValue = stage.name || stage.value;
                      const stageLabel = stage.display_name || stage.name || stage.label || stageValue;
                      const isSelected = contact.lifecycle_stage === stageValue;
                      const stageColor = stage.color || '#6b7280';
                      const reasons = getReasonsForLifecycle(stageValue);
                      const hasReasons = reasons.length > 0;
                      
                      return (
                        <Button
                          key={stageValue}
                          variant={isSelected ? 'default' : 'outline'}
                          className={`w-full justify-start text-left h-auto py-2.5 px-3 ${isSelected ? 'bg-slate-900' : ''}`}
                          onClick={() => {
                            if (hasReasons) {
                              setSelectedLifecycleForReason(stage);
                            } else {
                              handleLifecycleStageChange(stageValue);
                              setLifecycleSearchQuery('');
                            }
                          }}
                          data-testid={`lifecycle-${stageValue}`}
                        >
                          <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: stageColor }}
                              />
                              {isSelected && <CheckCircle size={14} className="flex-shrink-0 text-green-500" />}
                              <span className="truncate">{stageLabel}</span>
                            </div>
                            {hasReasons && (
                              <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
                            )}
                          </div>
                        </Button>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Owner Dialog */}
      <Dialog open={isOwnerDialogOpen} onOpenChange={(open) => {
        setIsOwnerDialogOpen(open);
        if (open) {
          // Always fetch fresh users when dialog opens
          fetchAllUsers();
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Change Owner</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-2">
              {isLoadingUsers ? (
                <div className="text-center py-8 text-slate-500">
                  <p>Loading team members...</p>
                </div>
              ) : allUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>No team members found</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={fetchAllUsers}>
                    Retry
                  </Button>
                </div>
              ) : (
                allUsers.map(u => (
                  <div
                    key={u.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      contact.owner_id === u.id ? 'bg-slate-100 border-2 border-slate-300' : 'hover:bg-slate-50 border-2 border-transparent'
                    }`}
                    onClick={() => handleOwnerChange(u.id)}
                    data-testid={`owner-${u.id}`}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-slate-200 text-slate-700 text-xs">
                        {u.full_name?.[0] || u.email?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{u.full_name || u.email}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                    </div>
                    {contact.owner_id === u.id && (
                      <Badge className="ml-auto">Current</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog open={isTagsDialogOpen} onOpenChange={(open) => { setIsTagsDialogOpen(open); if (!open) setTagSearchQuery(''); }}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} className="text-indigo-500" />
              Manage Tags
            </DialogTitle>
            <DialogDescription>
              Add or remove tags for this contact
            </DialogDescription>
          </DialogHeader>
          
          {/* Current Tags */}
          <div className="mb-4">
            <Label className="text-xs text-slate-500 mb-2 block">Current Tags</Label>
            <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 bg-slate-50 rounded-lg border">
              {(contact?.tags || []).length === 0 ? (
                <span className="text-xs text-slate-400">No tags assigned</span>
              ) : (
                (contact?.tags || []).map((tagName, idx) => {
                  const tagInfo = availableTags.find(t => t.tag_name === tagName);
                  const tagColor = tagInfo?.color || '#6366F1';
                  return (
                    <Badge 
                      key={idx}
                      className="text-xs font-normal"
                      style={{ 
                        backgroundColor: `${tagColor}20`,
                        color: tagColor,
                        borderColor: `${tagColor}40`
                      }}
                      variant="outline"
                    >
                      {tagName}
                      <X 
                        size={12} 
                        className="ml-1 cursor-pointer hover:opacity-70" 
                        onClick={() => handleRemoveTag(tagName)}
                      />
                    </Badge>
                  );
                })
              )}
            </div>
          </div>
          
          {/* Search and Add Tags */}
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Add Tags</Label>
            <div className="relative mb-2">
              <Input
                placeholder="Search tags..."
                value={tagSearchQuery}
                onChange={(e) => setTagSearchQuery(e.target.value)}
                className="w-full"
                data-testid="tag-search-input"
              />
              {tagSearchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setTagSearchQuery('')}
                >
                  <X size={14} />
                </Button>
              )}
            </div>
            
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-1">
                {filteredAvailableTags.length === 0 ? (
                  <p className="text-center text-slate-500 py-4 text-sm">
                    {tagSearchQuery ? 'No matching tags found' : 'All tags assigned or no tags available'}
                  </p>
                ) : (
                  filteredAvailableTags.map(tag => (
                    <Button
                      key={tag.id}
                      variant="ghost"
                      className="w-full justify-start text-left h-auto py-2 px-3 hover:bg-slate-50"
                      onClick={() => handleAddTag(tag)}
                      disabled={addingTag}
                      data-testid={`tag-option-${tag.name}`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tag.color || '#6366F1' }}
                        />
                        <span className="text-sm">{tag.name}</span>
                        {addingTag && <Loader2 size={12} className="ml-auto animate-spin" />}
                      </div>
                    </Button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Composer */}
      <EmailComposer
        isOpen={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        contact={contact}
        onEmailSent={() => { fetchEmails(); fetchActivities(); }}
      />

      {/* SMS Composer */}
      <SMSComposer
        isOpen={isSmsDialogOpen}
        onClose={() => setIsSmsDialogOpen(false)}
        contact={contact}
        onSmsSent={() => fetchActivities()}
      />

      {/* Call Dialog */}
      <Dialog open={isCallDialogOpen} onOpenChange={setIsCallDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall size={20} className="text-blue-500" />
              Make a Call
            </DialogTitle>
            <DialogDescription>
              We'll call your phone first, then connect you to {contact?.name || 'the contact'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="caller-phone">Your Phone Number</Label>
              <Input
                id="caller-phone"
                type="tel"
                placeholder="+971XXXXXXXXX"
                value={userPhone}
                onChange={(e) => setUserPhone(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Include country code (e.g., +971, +1, +44)
              </p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong>
              </p>
              <ol className="text-sm text-blue-700 mt-1 list-decimal list-inside space-y-1">
                <li>We'll call your phone first</li>
                <li>When you answer, we'll dial {contact?.name || 'the contact'}</li>
                <li>The call will be recorded for your records</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCallDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => initiateCallWithPhone(userPhone)}
              disabled={isInitiatingCall || !userPhone}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isInitiatingCall ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Calling...</>
              ) : (
                <><PhoneCall size={16} className="mr-2" /> Start Call</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Dialog */}
      <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Schedule Meeting</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                placeholder="Meeting title..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                placeholder="Meeting description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={newMeeting.start_time}
                  onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Duration (mins)</Label>
                <Select value={newMeeting.duration_minutes.toString()} onValueChange={(v) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Meeting Type</Label>
              <Select value={newMeeting.meeting_type} onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="in_person">In Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location / Link</Label>
              <Input
                value={newMeeting.location}
                onChange={(e) => setNewMeeting({ ...newMeeting, location: e.target.value })}
                placeholder="Meeting link or address..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMeetingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateMeeting} disabled={!newMeeting.title || !newMeeting.start_time}>
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cal.com Booking Modal */}
      <CalComBookingModal
        isOpen={isCalComDialogOpen}
        onClose={() => setIsCalComDialogOpen(false)}
        contact={contact}
        token={token}
        onBookingSuccess={() => {
          fetchMeetings();
          fetchActivities();
          setIsCalComDialogOpen(false);
        }}
      />

      {/* Post-Call Disposition Dialog */}
      <Dialog open={isPostCallDialogOpen} onOpenChange={setIsPostCallDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Bell className="animate-bounce" size={20} />
              Call Ended - Update Lead
            </DialogTitle>
            <DialogDescription>
              Please update the lead status, lifecycle stage, and add any notes from the call.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lead Status */}
            <div>
              <Label className="text-sm font-medium">Lead Status</Label>
              <div className="flex items-center gap-2 mt-1">
                <Badge 
                  className={`${statusStyles[contact?.status?.toLowerCase()] ? statusStyles[contact?.status?.toLowerCase()].bg + ' ' + statusStyles[contact?.status?.toLowerCase()].text : 'bg-slate-100 text-slate-700'} cursor-pointer`}
                  onClick={() => { setIsPostCallDialogOpen(false); setStatusSearchQuery(''); setIsStatusDialogOpen(true); }}
                >
                  {getStatusDisplayLabel(contact?.status)}
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => { setIsPostCallDialogOpen(false); setStatusSearchQuery(''); setIsStatusDialogOpen(true); }}
                >
                  Change
                </Button>
              </div>
            </div>

            {/* Lifecycle Stage */}
            <div>
              <Label className="text-sm font-medium">Lifecycle Stage</Label>
              <div className="flex items-center gap-2 mt-1">
                <Badge 
                  variant="outline" 
                  className="bg-purple-50 text-purple-700 border-purple-200 cursor-pointer"
                  onClick={() => { setIsPostCallDialogOpen(false); setLifecycleSearchQuery(''); setIsLifecycleStageDialogOpen(true); }}
                >
                  {getLifecycleDisplayLabel(contact?.lifecycle_stage)}
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => { setIsPostCallDialogOpen(false); setLifecycleSearchQuery(''); setIsLifecycleStageDialogOpen(true); }}
                >
                  Change
                </Button>
              </div>
            </div>

            {/* Call Note */}
            <div>
              <Label className="text-sm font-medium">Call Notes</Label>
              <Textarea
                value={postCallData.note}
                onChange={(e) => setPostCallData({ ...postCallData, note: e.target.value })}
                placeholder="Enter notes from the call... What was discussed? Next steps?"
                className="mt-1 min-h-[100px]"
                data-testid="post-call-note"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsPostCallDialogOpen(false)}
            >
              Skip
            </Button>
            <Button 
              onClick={handlePostCallSave}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle size={16} className="mr-1" />
              Save & Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactDetailRedesigned;
